import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { CookieService } from 'ngx-cookie';
import { ToastrService } from 'ngx-toastr';

import { AES, enc } from 'crypto-js';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import jwt_decode from 'jwt-decode';
import { escapeRegExp, find, findIndex, intersectionBy, orderBy, toLower } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';

import { companyTypeConstants, companyTypes, config, inspectionItemColorIndex, orderStatusLabel, tirePositionOptions, units } from 'app/constants';
import { timezoneName, timezones } from 'app/constants/timezone';

import { environment } from 'environments/environment';

import { currency } from 'app/constants/currency.constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { styles } from 'app/constants/oil-sticker-styling';
import { isOilStickerImprovedstyles } from 'app/constants/oil-sticker-styling-improved';
import { itemOrderStatusLabel } from 'app/constants/orders.constants';
import { CalculationService } from 'app/services/calculation.service';
import { DataService } from 'app/services/data.service';
import { DateService } from 'app/services/date.service';
import { SharedService } from 'app/services/shared.service';
import { AllowDecimalMask } from './sharedInterfaces';
/**
 * Helper Service
 */
@Injectable()
export class HelperService {
  confirmationSubject: Subject<any> = new Subject<any>();
  hideBottomDetailsInTechApp: Subject<boolean> = new Subject<boolean>();
  documentClickedTarget: Subject<HTMLElement> = new Subject<HTMLElement>();
  weekday = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };

  private encrypted: Boolean = environment.encrypted;

  /**
   * @param  {CookieService} cookieService Cookie Service
   * @param  {Router} router Router
   * @param  {ToastrService} toastrService Toastr Service
   * @param dateService
   * @param calculateService
   * @param currencyPipe
   * @param dataService
   * @param sharedService
   */
  constructor(
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private dateService: DateService,
    private calculateService: CalculationService,
    private currencyPipe: CurrencyPipe,
    private router: Router,
    private dataService: DataService,
    private sharedService: SharedService,
    private calculationService: CalculationService,
  ) { }

  jwtDecode(token: any): any {
    return jwt_decode(token);
  }

  getCompany() {
    const alpat = this.cookieService.getObject('alpat');

    if (!alpat) return {};

    const token: any = this.jwtDecode(alpat);
    let shopId = token.shopId;
    const currentShopId = localStorage.getItem('currentShop');
    if (token.isMultiLocationEnabled && currentShopId) shopId = currentShopId;
    if (token) {
      return {
        currentShopId,
        companyId: token.companyId,
        companyType: token.companyType,
        companyEmail: token.email,
        shopId,
        companyName: token.name,
        companyPhone: token.phone,
        logo: token.logo,
        userName: token.fullName,
        isSupportUser: token.isSupportUser,
        role: token.role,
        stateCode: token.stateCode,
        userId: token.id,
        emplyeeId: token.employeeId,
        employeeId: token.employeeId,
        timezone: token.timezone,
        qboId: token.qboId,
        corporateId: token.corporateId,
        qbo: token.QBO,
        iat: token.iat,
        isDTS: token.isDTS,
        isWorkAsTechEnabled: token.isWorkAsTechEnabled,
        hasMultiLocationAccess: token.hasMultiLocationAccess,
        isMultiLocationEnabled: token.isMultiLocationEnabled,
        isRevenueRecognitionSettleEnabled: token.isRevenueRecognitionSettleEnabled,
        isShopTzUseEnabled: token.isShopTzUseEnabled,
      };
    } else {
      return {};
    }
  }

  encryptPaymentData(data) {
    const d = AES.encrypt(JSON.stringify(data), enc.Utf8.parse(environment.payemntKey), { iv: enc.Utf8.parse(environment.payemntKey) });
    return { d: d.toString() };
  }

  getLocalizationValue(currentLang: string, value: any, key: string, skipFallBack?: boolean): string {
    const localizedKey = toLower(currentLang) === 'fr' ? `${key}FR` : key;
    const fallbackKey = toLower(currentLang) === 'fr' ? key : `${key}FR`;
    if (value) {
      if (skipFallBack) {
        return value[localizedKey] || '';
      } else {
        return value[localizedKey] || value[fallbackKey] || '';
      }
    }
  }

  getCurrentShopId() {
    let { shopId, isMultiLocationEnabled } = this.getCompany();
    if (isMultiLocationEnabled) {
      shopId = this.dataService.currentShopId;
    }
    return shopId;
  }

  decryptPaymentData(data) {
    const bytes = AES.decrypt(data, environment.payemntKey);
    return JSON.parse(bytes.toString(enc.Utf8));
  }

  generateOnbStepCompanyName = (companyData: any = {}) => {
    let name = companyData.name || '';
    if (companyData.companyType === companyTypeConstants.GUIDED_TRIALS && companyData.trial && companyData.trial.endDate && new Date(companyData.trial.endDate).getTime() < new Date().getTime()) {
      name = `${name} - Expired (Trial ID:${companyData.trial.sfId})`;
    }

    return name;
  };

  showToaster(type: string, msg: string) {
    if (type === 'success') {
      this.toastrService.success(
        `<i class="fal fa-check custom-check-icon"></i>
      ${msg} <i class="fal fa-times custom-close-icon"></i>`,
        '',
        config.toasterConfig,
      );
    } else if (type === 'error') {
      this.toastrService.error(
        `<i class="fal fa-exclamation-triangle custom-check-icon"></i>
        ${msg} <i class="fal fa-times custom-close-icon"></i>`,
        '',
        config.toasterConfig,
      );
    }
  }

  showCenterToaster(type: string, msg: string) {
    if (type === 'error') {
      this.toastrService.error(
        `<i class="fal fa-exclamation-triangle custom-check-icon"></i>
        ${msg} <i class="fal fa-times custom-close-icon"></i>`,
        '',
        config.toasterCenterConfig,
      );
    }
  }

  encryptData = (data) => {
    if (this.encrypted) {
      const d = AES.encrypt(JSON.stringify(data), enc.Utf8.parse(environment.adatak), { iv: enc.Utf8.parse(environment.bdatak) });
      return { d: d.toString() };
    } else {
      return data;
    }
  };

  decryptData = (data) => {
    if (this.encrypted) {
      const bytes = AES.decrypt(data.d.toString(), enc.Utf8.parse(environment.adatak), { iv: enc.Utf8.parse(environment.bdatak) });
      const decryptedData = JSON.parse(bytes.toString(enc.Utf8));
      return decryptedData;
    } else {
      return data;
    }
  };

  arraysAllObjectsEqual(arr) {
    if (!Array.isArray(arr) || arr.length <= 1) {
      return true;
    }

    const firstElement = JSON.stringify(arr[0]);

    for (let i = 1; i < arr.length; i++) {
      // if (!isEqual(arr[0], arr[i])) return false;
      if (JSON.stringify(arr[i]) !== firstElement) {
        console.log(arr[i]);

        return false;
      }
    }

    return true;
  }

  setInspectionItemsCounts(inspection: any) {
    const counts = {
      categories: 0,
      items: 0,
      condition: {
        good: 0,
        normal: 0,
        bad: 0,
        notInspected: 0,
      },
      images: 0,
      audios: 0,
      videos: 0,
    };

    if (inspection && inspection.category && inspection.category.length) {
      counts.categories = inspection.category.length;

      inspection.category.forEach((category: any) => {
        if (category.inspectionItems.length) {
          counts.items += category.inspectionItems.length;
          category.inspectionItems.forEach(({ attachments, colorIndex }: any) => {
            if (colorIndex === -1) {
              counts.condition.notInspected += 1;
            } else {
              counts.condition[inspectionItemColorIndex[colorIndex]] += 1;
            }

            if (attachments && attachments.length) {
              attachments.forEach(({ type }) => {
                if (type === 'image') {
                  counts.images += 1;
                } else if (type === 'voice') {
                  counts.audios += 1;
                } else if (type === 'video') {
                  counts.videos += 1;
                }
              });
            }
          });
        }
      });
    }

    return counts;
  }

  saveLocalStorage(key, obj) {
    localStorage.setItem(key, JSON.stringify(obj));
  }

  getLocalStorage(key) {
    const d = localStorage.getItem(key);
    return JSON.parse(d);
  }

  removeFromStorage(key) {
    localStorage.removeItem(key);
  }

  trimString(str: string) {
    try {
      if (str) {
        str = `${str || ''}`.trim();
        str = str.replace(/\s\s+/g, ' ');
      }

      return str;
    } catch (err) {
      console.log(err);
      return str;
    }
  }

  todayDate() {
    return moment().toDate();
  }

  convertDate(date) {
    return moment(date);
  }

  durationBtwTwoDatesMili(start, end) {
    return moment.duration(end.diff(start)).asMilliseconds();
  }

  // dateFormatDDMMMYY(date) {
  //   return moment(date).format('DD-MMM-YY');
  // }

  dateFormatMMMDDYY(date) {
    return moment(date).format('MMM DD, YYYY');
  }

  dateFormatDDMMYYhhmmssa(date) {
    return moment(date).format('DD/MM/YY h:mm:ssa');
  }

  // dateFormatMMMDDYYhhmmssa(date) {
  //   return moment(date).format('MMM DD, YYYY h:mm:ssa');
  // }

  dateFormathhmma(date) {
    return moment(date).format('h:mm a');
  }

  dateFormat(format, date?) {
    if (date) {
      return moment(date).format(format);
    }
    return moment().format(format);
  }

  formatPhoneNumber(str) {
    // Filter only numbers from the input
    const cleaned = ('' + str).replace(/\D/g, '');

    // Check if the input is of correct length
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return '';
  }

  timeDropdown() {
    const x = 15;
    const times = [];
    let tt = 0;
    const ap = ['am', 'pm'];

    for (let i = 0; tt < 24 * 60; i++) {
      const hh = Math.floor(tt / 60); // getting hours of day in 0-24 format
      const mm = tt % 60; // getting minutes of the hour in 0-55 format
      const hour = hh;
      times[i] = {
        title: ('0' + (hh % 12)).slice(-2) + ':' + ('0' + mm).slice(-2) + ' ' + ap[Math.floor(hh / 12)],
        value: `${hour}:${mm}`,
        // value: {
        //     hour: hour,
        //     min: mm
        // }
      };
      tt = tt + x;
    }

    return times;
  }

  /** Get company details from the token  */

  updateToken(token) { }

  getLoggedInUserRole() {
    const token: any = this.jwtDecode(this.cookieService.getObject('alpat'));
    if (token) {
      return token.role;
    } else {
    }
  }

  zipCodeSearch(key, array) {
    for (let i = 0; i < array.length; i++) {
      if (array[i].Code === key) {
        return array[i];
      }
    }
  }

  motorSig(data: any) {
    const Sig = data.pKey + '\n' + 'GET' + '\n' + data.date + '\n' + data.url;
    const hash = hmacSHA256(Sig, data.akey);
    const hashInBase64 = enc.Base64.stringify(hash);
    return encodeURIComponent(hashInBase64);
  }

  getErrorMsg(error) {
    if (typeof error === 'string') {
      return error;
    } else if (typeof error === 'object') {
      if (error && error.name && error.name === 'HttpErrorResponse') {
        return 'Please check your internet.';
      }
    } else {
      return error;
    }
  }

  checkForToday(dataTimeString) {
    return this.convertDate(dataTimeString).isSame(moment(), 'day');
  }

  getTime(dataTimeString) {
    return this.convertDate(dataTimeString).format('hh:mm a');
  }

  getDate(dataTimeString: any) {
    return this.convertDate(dataTimeString).format('MMM DD, YYYY');
  }
  dateFormatFrench(date) {
    return moment(date).locale('fr').format('MMMM DD, YYYY').replace('.', '');
  }
  dateFrenchWithFormate(date, formate: string) {
    return moment(date).locale('fr').format(formate).replace('.', '');
  }
  getDateByFormat(dataTimeString: any, format: string) {
    return this.convertDate(dataTimeString).format(format ?? 'MMM DD, YYYY');
  }
  convertTimeToNumber(time: any): number {
    if (!time) {
      return null;
    }
    if (typeof time !== 'string') {
      time = time.toString();
    }
    return Number(time.split(' ')[1].split(':').join('.'));
  }
  removeSpaceFromString(inputString) {
    return inputString.replace(/\s/g, '');
  }

  itemEmpty(item) {
    if (item.title === null || item.count === 0 || item.price === null) {
      return true;
    } else {
      return false;
    }
  }

  itemNotEmpty(item) {
    if (item.title !== null || item.count !== 0 || item.price !== null) {
      if (item.title !== null && item.count !== 0 && item.count !== '' && item.count !== undefined && item.price !== null) {
        return item;
      } else {
        return false;
      }
    } else {
      return 'empty';
    }
  }

  /**
   * To insert a text at cursor position
   * @param el textarea in which you want to insert the text
   * @param newText text to be inserted
   */
  insertTextInTextArea(el, newText) {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = el.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    el.value = before + newText + after;
    el.selectionStart = el.selectionEnd = start + newText.length;
    el.focus();
    return el.value;
  }

  fetchInitials(name: string = '') {
    try {
      let initials = '';

      if (name && name.length) {
        const [firstName, lastName] = name.split(' ');

        if (firstName) initials = firstName.slice(0, 1);
        if (lastName) initials = initials + lastName.slice(0, 1);
        if (!lastName) initials = initials + firstName.slice(1, 2);
      }

      return initials;
    } catch (err) {
      return '';
    }
  }

  firstNameAndLastInitial(name) {
    let initials = '';
    let firstName = '',
      lastName = '';
    if (name) {
      [firstName, lastName] = name.split(' ');
      initials = `${firstName} ${this.fetchInitials(lastName)}`;
    }
    return initials;
  }

  fromNow(data) {
    return this.convertDate(data).fromNow();
  }

  fromNowInDays(data) {
    const currentMili = new Date().getTime();
    const wfMili = new Date(data).getTime();
    const diff = currentMili - wfMili;
    const days = diff / (1000 * 60 * 60 * 24);

    return days;
  }

  getCustomerFirstName(customer: any) {
    if (customer) {
      return customer.split(' ')[0].trim() || '';
    }
  }

  /**
   * @param  {String} status Status
   */

  estimateStatusMap(status) {
    if (status === 'created') {
      return 'In Progress';
    } else if (status === 'pending' || status === 'approve') {
      return 'Estimate';
    } else if (status === 'invoiced') {
      return 'Invoiced';
    } else if (status === 'paid') {
      return 'Paid';
    } else {
      return status;
    }
  }

  /**
   * Take company type constant key and return appropriate title
   * @param companyType company type constant
   */
  companyTypeToTitleMap(companyType: String, wrapInBrackets = false) {
    const type = companyTypes.filter((ct) => ct.value === companyType)[0];
    return type ? (wrapInBrackets ? `(${type.title})` : type.title) : '';
  }

  serviceInspectionStatusMap(status) {
    if (status === 'new') {
      return 'Not Started';
    } else if (status === 'pending') {
      return 'Pending';
    } else if (status === 'inprogress') {
      return 'In Progress';
    } else if (status === 'pause') {
      return 'Paused';
    } else if (status === 'completed') {
      return 'Completed';
    } else {
      return status;
    }
  }

  templateParser(template: string, data: any, isApptTitleAttr: boolean = false, gTimezone: any = null, currentLang : string = 'en') {
    currentLang = currentLang.toLowerCase();
    let str: string = template;
    let _timezones = timezones;

    if (!str) {
      return str;
    }

    if (gTimezone) {
      _timezones = gTimezone;
    }

    try {
      // Company
      if (data.company) {
        const companyName = data.company.shop ? (data.company.shop.name ? data.company.shop.name : data.company.shop.companyName) : data.company.name ? data.company.name : data.company.companyName;
        const companyEmail = data.company.shop ? data.company.shop.email : data.company.name;
        const companyPhone = data.company.shop ? data.company.shop.phone : data.company.phone;
        const companyPhone1 = data.company.shop ? data.company.shop.phone1 : data.company.phone1;

        str = str.replace(new RegExp('\\[company_name\\]', 'g'), companyName);
        str = str.replace(new RegExp('\\[company_email\\]', 'g'), companyEmail);
        str = str.replace(new RegExp('\\[company_contact_1\\]', 'g'), this.formatPhoneNumber(companyPhone));
        str = str.replace(new RegExp('\\[company_contact_2\\]', 'g'), this.formatPhoneNumber(companyPhone1) || '');
        str = str.replace(new RegExp('\\[company_website\\]', 'g'), data.company.website || '');
        str = str.replace(new RegExp('\\[logo\\]', 'g'), data.company.logo || '');

        if (data.company.address) {
          const country = data.company.address.country;
          let timezone = '';
          if (country) {
            if (data.company.timezoneId) {
              timezone = _timezones[country.toUpperCase()].find((time) => time.timezoneId === data.company.timezoneId)?.value;
            } else {
              timezone = timezoneName[country][data.company.timezone];
            }
          }
          str = str.replace(new RegExp('\\[company_address_1\\]', 'g'), data.company.address.addLine1 || '');
          str = str.replace(new RegExp('\\[company_address_2\\]', 'g'), data.company.address.addLine2 || '');
          str = str.replace(new RegExp('\\[company_city\\]', 'g'), data.company.address.city || '');
          str = str.replace(new RegExp('\\[company_state_code\\]', 'g'), data.company.address.stateCode || '');
          str = str.replace(new RegExp('\\[company_country\\]', 'g'), data.company.address.country || '');
          str = str.replace(new RegExp('\\[company_zip\\]', 'g'), data.company.address.zip || '');
          str = str.replace(new RegExp('\\[company_location\\]', 'g'), data.company.location || '');
          str = str.replace(new RegExp('\\[company_timezone_code\\]', 'g'), timezone || '');
        }
      }

      // Appointments
      if (data.appointment) {
        if (data.appointment.title) {
          if (isApptTitleAttr) {
            str = str.replace(new RegExp('<appt>\\[appointment_title\\]</appt>', 'g'), `<appt>${data.appointment.title}</appt>` || '<appt>[appointment_title]</appt>');
          } else {
            str = str.replace(new RegExp('\\[appointment_title\\]', 'g'), `${data.appointment.title}` || '[appointment_title]');
          }
        }
        str = str.replace(new RegExp('\\[start_date\\]', 'g'), currentLang === 'fr' ? this.dateService.formatDate(data.appointment.startDate, datetimeFormat.mmmmdyyyy, 'fr') : this.dateService.formatDate(data.appointment.startDate, datetimeFormat.mmmdyyyy) || '');
        str = str.replace(new RegExp('\\[start_time\\]', 'g'), data.appointment.startTime || '');
        str = str.replace(new RegExp('\\[start_day\\]', 'g'), `${this.dateService.getDay(data.appointment.startDate)}` || '');
        str = str.replace(new RegExp('\\[end_date\\]', 'g'), currentLang === 'fr' ? this.dateService.formatDate(data.appointment.endDate, datetimeFormat.mmmmdyyyy, 'fr') : this.dateService.formatDate(data.appointment.endDate, datetimeFormat.mmmdyyyy) || '');
        str = str.replace(new RegExp('\\[end_time\\]', 'g'), data.appointment.endTime || '');
        str = str.replace(new RegExp('\\[end_day\\]', 'g'), `${this.dateService.getDay(data.appointment.endDate)}` || '');
      }

      // Customer
      if (data.customer) {
        let firstName = data.customer.firstName || '';
        let lastName = data.customer.lastName || '';

        if (!firstName && data.customer.fullName) {
          const name: string[] = data.customer.fullName.trim().split(' ');
          firstName = name[0];
          lastName = name.slice(1).join(' ');
        }

        if (data.customer.isFleetCustomer && data.customer.fullName) {
          firstName = data.customer.fullName;
          lastName = '';
        }

        str = str.replace(new RegExp('\\[customer_first_name\\]', 'g'), firstName);
        str = str.replace(new RegExp('\\[customer_last_name\\]', 'g'), lastName);
        str = str.replace(new RegExp('\\[customer_email\\]', 'g'), data.customer.email || '');
        str = str.replace(new RegExp('\\[customer_mobile\\]', 'g'), this.formatPhoneNumber(data.customer.phone) || '');
      }

      // Vehicle
      if (data.vehicle && data.vehicle.year) {
        str = str.replace(new RegExp('\\[vehicle_year\\]', 'g'), data.vehicle.year || '');
        str = str.replace(new RegExp('\\[vehicle_make\\]', 'g'), data.vehicle.make || '');
        str = str.replace(new RegExp('\\[vehicle_model\\]', 'g'), data.vehicle.model || '');
        str = str.replace(new RegExp('\\[vehicle_engine\\]', 'g'), data.vehicle.engine || '');
      }

      // Payment
      if (data.payment) {
        const paymentAmount = this.calculateService.roundOffToDecimalPlaces(data.payment.amount || 0);
        const patmentDateTime = this.convertTimezoneForRender(data.payment.createdAt, 'MM-DD-YYYY HH:mm').date;

        str = str.replace(new RegExp('\\[payment_amount\\]', 'g'), `$${paymentAmount}`);
        str = str.replace(new RegExp('\\[payment_receipt_number\\]', 'g'), data.payment.previewLink || '');
        str = str.replace(new RegExp('\\[payment_receipt_number\\]', 'g'), data.payment.code || '');
        str = str.replace(new RegExp('\\[payment_method\\]', 'g'), data.payment.paymentType || '');
        str = str.replace(new RegExp('\\[payment_date\\]', 'g'), this.dateService.formatDate(patmentDateTime, datetimeFormat.mmmdyyyy) || '');
        str = str.replace(new RegExp('\\[payment_time\\]', 'g'), this.dateFormathhmma(patmentDateTime) || '');
      } else {
        str = str.replace(new RegExp('\\[payment_amount\\]', 'g'), '');
        str = str.replace(new RegExp('\\[payment_receipt_number\\]', 'g'), '');
        str = str.replace(new RegExp('\\[payment_method\\]', 'g'), '');
        str = str.replace(new RegExp('\\[payment_date\\]', 'g'), '');
        str = str.replace(new RegExp('\\[payment_time\\]', 'g'), '');
      }

      // Pickup Date Time
      if (data.pickUpDateTime) {
        const pickupDate = this.getDate(data.pickUpDateTime);
        const pickupTime = this.getTime(data.pickUpDateTime);
        const pickupDay = this.dateService.getDay(data.pickUpDateTime);

        str = str.replace(new RegExp('\\[pickup_date\\]', 'g'), pickupDate || '');
        str = str.replace(new RegExp('\\[pickup_time\\]', 'g'), pickupTime || '');
        str = str.replace(new RegExp('\\[pickup_day\\]', 'g'), pickupDay || '');
      } else {
        str = str.replace(new RegExp('\\[pickup_date\\]', 'g'), '');
        str = str.replace(new RegExp('\\[pickup_time\\]', 'g'), '');
        str = str.replace(new RegExp('\\[pickup_day\\]', 'g'), '');
      }

      // Statements
      if (data.statement) {
        str = str.replace(new RegExp('\\[statement_name\\]', 'g'), data.statement.name || '');

        if (data.statement.startDate) {
          const startDate = this.dateService.formatDate(new Date(data.statement.startDate).getTime(), datetimeFormat.mmmdyyyy);
          str = str.replace(new RegExp('\\[statement_start_date\\]', 'g'), startDate || '');
        }

        if (data.statement.endDate) {
          const endDate = this.dateService.formatDate(new Date(data.statement.endDate).getTime(), datetimeFormat.mmmdyyyy);
          str = str.replace(new RegExp('\\[statement_end_date\\]', 'g'), endDate || '');
        }
      }

      // Review
      const currentDateTime = this.convertTimezoneForRender(new Date(), 'MM-DD-YYYY HH:mm');
      const reviewDate = this.getDate(currentDateTime);
      const reviewTime = this.getTime(currentDateTime);
      const reviewDay = this.dateService.getDay(reviewDate);

      str = str.replace(new RegExp('\\[review_request_date\\]', 'g'), reviewDate || '');
      str = str.replace(new RegExp('\\[review_request_time\\]', 'g'), reviewTime || '');
      str = str.replace(new RegExp('\\[review_request_day\\]', 'g'), reviewDay || '');

      // Order
      if (data.order) {
        const total = this.calculateService.roundOffToDecimalPlaces(data.order.total || 0);
        const paid = this.calculateService.roundOffToDecimalPlaces(data.order.paid || 0);
        const remaining = this.calculateService.roundOffToDecimalPlaces(data.order.total - data.order.paid || 0);
        const depositAmt = this.calculateService.roundOffToDecimalPlaces(data.order.deposit_amount || 0);

        str = str.replace(new RegExp('\\[order_title\\]', 'g'), data.order.title || '');
        str = str.replace(new RegExp('\\[order_number\\]', 'g'), data.order.code || '');
        str = str.replace(new RegExp('\\[invoice_number\\]', 'g'), data.order.invCode || data.order.code);
        str = str.replace(new RegExp('\\[order_authorization_status\\]', 'g'), data.order.authStatus || '');
        str = str.replace(new RegExp('\\[order_status\\]', 'g'), data.order.orderStatus || '');
        str = str.replace(new RegExp('\\[order_total\\]', 'g'), `$${total}`);
        str = str.replace(new RegExp('\\[order_total_paid\\]', 'g'), `$${paid}`);
        str = str.replace(new RegExp('\\[deposit_amount\\]', 'g'), `$${depositAmt}`);
        str = str.replace(new RegExp('\\[order_remaining_balance\\]', 'g'), `$${remaining}`);
      } else {
        str = str.replace(new RegExp('\\[order_title\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_number\\]', 'g'), '');
        str = str.replace(new RegExp('\\[invoice_number\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_authorization_status\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_status\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_total\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_total_paid\\]', 'g'), '');
        str = str.replace(new RegExp('\\[deposit_amount\\]', 'g'), '');
        str = str.replace(new RegExp('\\[order_remaining_balance\\]', 'g'), '');
      }
    } catch (err) {
      console.log(err);
    }

    return str;
  }

  revertTemplateParsing(template, data, isApptTitleAttr: boolean = false) {
    let str: string = template;

    if (!str) {
      return;
    }
    try {
      str = str.replace(new RegExp('\\[msg_content\\]', 'g'), data.msg_content || '');
      str = str.replace(/<!--- -->/g, '');
      /** Company */
      if (data.company) {
        const companyName = data.company.name ? data.company.name : data.company.companyName;
        str = str.replace(new RegExp(companyName || 'invalid_tag', 'g'), '[company_name]');
        str = str.replace(new RegExp(data.company.email || 'invalid_tag', 'g'), '[company_email]');
        str = str.replace(new RegExp(data.company.phone || 'invalid_tag', 'g'), '[company_contact_1]');
        str = str.replace(new RegExp(data.company.website || 'invalid_tag', 'g'), '[company_website]');
        str = str.replace(new RegExp(data.company.logo || 'invalid_tag', 'g'), '[logo]');

        if (data.company.address) {
          str = str.replace(new RegExp(data.company.address.addLine1 || 'invalid_tag', 'g'), '[company_address_1]');
          str = str.replace(new RegExp(data.company.address.addLine2 || 'invalid_tag', 'g'), '[company_address_2]');
          str = str.replace(new RegExp(data.company.address.city || 'invalid_tag', 'g'), '[company_city]');
          str = str.replace(new RegExp(data.company.stateCode || 'invalid_tag', 'g'), '[company_state_code]');
          str = str.replace(new RegExp(data.company.country || 'invalid_tag', 'g'), '[company_country]');
          str = str.replace(new RegExp(data.company.address.zip || 'invalid_tag', 'g'), '[company_zip]');
          str = str.replace(new RegExp(data.company.location || 'invalid_tag', 'g'), '[company_location]');
          str = str.replace(new RegExp(data.company.timezone || 'invalid_tag', 'g'), '[company_timezone_code]');
        }
      }

      /** Appointment */
      if (data.appointment) {
        if (isApptTitleAttr) {
          str = str.replace(new RegExp(this.escapeRegex(`<appt>${data.appointment.title}</appt>`) || 'invalid_tag', 'g'), '<appt>[appointment_title]</appt>');
        } else {
          str = str.replace(new RegExp(this.escapeRegex(`${data.appointment.title}`) || 'invalid_tag', 'g'), '[appointment_title]');
        }
        str = str.replace(new RegExp(this.dateService.formatDate(data.appointment.startDate, datetimeFormat.mmmdyyyy) || 'invalid_tag', 'g'), '[start_date]');
        str = str.replace(new RegExp(data.appointment.startTime || 'invalid_tag', 'g'), '[start_time]');
        str = str.replace(new RegExp(this.escapeRegex(this.dateService.getDay(data.appointment.startDate)) || 'invalid_tag', 'g'), '[start_day]');
        str = str.replace(new RegExp(this.dateService.formatDate(data.appointment.endDate, datetimeFormat.mmmdyyyy) || 'invalid_tag', 'g'), '[end_date]');
        str = str.replace(new RegExp(data.appointment.endTime || 'invalid_tag', 'g'), '[end_time]');
        str = str.replace(new RegExp(this.escapeRegex(this.dateService.getDay(data.appointment.endDate)) || 'invalid_tag', 'g'), '[end_day]');
        str = str.replace('&nbsp;', ' ');
        str = str.replace('nbsp;', ' ');
      }

      /** Customer */
      if (data.customer) {
        let firstName = data.customer.firstName;
        let lastName = /\p{L}/u.test(data.customer.lastName) ? data.customer.lastName : '';
        if (data.customer.fullName) {
          const name: string[] = data.customer.fullName.split(' ');
          firstName = name[0];
          lastName = name.slice(1).join(' ');
        }
        str = str.replace(new RegExp(firstName || 'invalid_tag', 'g'), '[customer_first_name]');
        str = str.replace(new RegExp(lastName || 'invalid_tag', 'g'), '[customer_last_name]');
        str = str.replace(new RegExp(data.customer.email || 'invalid_tag', 'g'), '[customer_email]');
        str = str.replace(new RegExp(data.customer.phone || 'invalid_tag', 'g'), '[customer_mobile]');
      }

      /** Pickup Date */
      if (data.pickUpDateTime) {
        const pickupDate = this.getDate(data.pickUpDateTime);
        const pickupTime = this.getTime(data.pickUpDateTime);
        const pickupDay = this.dateService.getDay(data.pickUpDateTime);
        str = str.replace(new RegExp(pickupDate || 'invalid_tag', 'g'), '[pickup_date]');
        str = str.replace(new RegExp(pickupTime || 'invalid_tag', 'g'), '[pickup_time]');
        str = str.replace(new RegExp(pickupDay || 'invalid_tag', 'g'), '[pickup_day]');
      }

      /** Vehicle */
      if (data.vehicle && data.vehicle.year) {
        str = str.replace(new RegExp(escapeRegExp(data.vehicle.year || 'invalid_tag'), 'g'), '[vehicle_year]');
        str = str.replace(new RegExp(escapeRegExp(data.vehicle.make || 'invalid_tag'), 'g'), '[vehicle_make]');
        str = str.replace(new RegExp(escapeRegExp(data.vehicle.model || 'invalid_tag'), 'g'), '[vehicle_model]');
        str = str.replace(new RegExp(escapeRegExp(data.vehicle.engine || 'invalid_tag'), 'g'), '[vehicle_engine]');
      }

      /** Vehicle */
      if (data.statement) {
        str = str.replace(new RegExp(data.statement.name || 'invalid_tag', 'g'), '[statement_name]');

        if (data.statement.startDate) {
          const startDate = this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(data.statement.startDate).getTime()), datetimeFormat.mmmdyyyy);
          str = str.replace(new RegExp(startDate || 'invalid_tag', 'g'), '[statement_start_date]');
        }

        if (data.statement.endDate) {
          const endDate = this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(data.statement.endDate).getTime()), datetimeFormat.mmmdyyyy);
          str = str.replace(new RegExp(endDate || 'invalid_tag', 'g'), '[statement_end_date]');
        }
      }
    } catch (err) { }

    return str;
  }

  parseNotificationTemplate(data: any, content) {
    let str = content;
    if (data.title) {
      str = str.replace(new RegExp('\\[title\\]', 'g'), data.title);
    }

    if (data.actionBy) {
      str = str.replace(new RegExp('\\[action_by\\]', 'g'), data.actionBy);
    }

    if (data.eventDateTime) {
      str = str.replace(new RegExp('\\[event_date_time\\]', 'g'), moment(data.eventDateTime).format('lll'));
    }

    return str;
  }

  removePhoneFormatting(phone: string = '') {
    return `${phone}`.replace(/\s|-|\(|\)/g, '');
  }

  sortCitiesByState(cities) {
    const companyStateCode = this.getCompany().stateCode;
    return cities.sort((aCity) => (aCity.stateCode === companyStateCode ? -1 : 1));
  }

  /**
   * To covert date time to tenant timezone
   * @param date incoming date from selection
   * @param time incoming time from selection
   * @param timezone text to be inserted
   */
  convertDateTimeToTimezone(date, time) {
    date = new Date(this.convertTimezoneForRender(date, null, false, true).date.toString());
    let dateInString;
    const month = date.getMonth() < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
    const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
    if (time !== null) {
      dateInString = `${date.getUTCFullYear()}-${month}-${day} ${time.split(':')[0]}:${time.split(':')[1]}`;
    } else {
      const mins = date.getMinutes() === 0 ? '00' : date.getMinutes();
      dateInString = `${date.getUTCFullYear()}-${month}-${day} ${date.getHours()}:${mins}`;
    }
    return dateInString;
  }

  convertTimezoneForRender(date, format = null, isAppt = false, isCurrent = false, isMoment = false, timezone = null) {
    try {
      let offset;

      if (timezone === null) {
        offset = parseInt(this.getCompany().timezone.replace('GMT', ''));
      } else {
        offset = parseInt(timezone.replace('GMT', ''));
      }

      // create Date object for current location
      const d = new Date(date);
      // convert to msec
      // subtract local time zone offset
      // get UTC time in msec
      const utc = d.getTime() + d.getTimezoneOffset() * 60000;
      // create new Date object for different city
      // using supplied offset
      const nd = new Date(utc + 3600000 * offset);
      // return time as a string
      // return nd;
      const fm = 'DD-MM-YYYY h:mma';
      if (isCurrent) {
        return { date: new Date(nd) };
      } else {
        if (!isAppt) {
          if (!format) {
            if (!isMoment) {
              return {
                date: moment(nd).format(fm).split(' ')[0],
                time: `${moment(nd).format(fm).split(' ')[1]}`,
              };
            } else {
              return { date: moment(nd) };
            }
          } else {
            const fm = format;
            return {
              date: moment(nd).format(fm),
              time: `${moment(nd).format(fm).split(' ')[1]} ${moment(nd).format(fm).split(' ')[2]}`,
            };
          }
        } else {
          return {
            date: moment(nd).date(),
            month: moment(nd).format('MMM'),
            time: moment(nd).format('h:mma'),
          };
        }
      }
    } catch (err) {
      const currentDate = new Date();
      return {
        date: moment(currentDate).date(),
        month: moment(currentDate).format('MMM'),
        time: moment(currentDate).format('h:mma'),
      };
    }
  }

  formateDate(date) {
    const d = new Date(date);
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    let day;
    if (d.getDay() === 0) {
      day = days[days.length - 1];
    } else {
      day = days[d.getDay() - 1];
    }
    const month = d.toLocaleString('default', { month: 'short' });
    return d.getFullYear() === new Date().getFullYear() ? `${day}, ${d.getDate()} ${month}` : `${day}, ${d.getDate()} ${month}, ${d.getFullYear()}`;
  }

  formateTime(time) {
    /* incoming time format : 01:00 pm*/
    if (time) {
      const timeArr = time.split(' ');
      const timePart = timeArr[0].split(':');
      return parseInt(timePart[0]) < 9 ? `${parseInt(timePart[0])}:${timePart[1]}${timeArr[1].toLocaleLowerCase()}` : `${timePart[0]}:${timePart[1]}${timeArr[1].toLocaleLowerCase()}`;
    }
  }

  getFormattedTime(second: any) {
    const min = parseInt((second / 60).toString(), 10);
    const sec = second % 60;
    return ` ${min > 9 ? min : `0${min}`} : ${sec > 9 ? sec : `0${sec}`}`;
  }

  dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], `${new Date().getTime()}.png`, {
      type: 'image/jpeg',
    });
  }

  /** open window in new tab */
  openPageInNewTab(path: string) {
    const url = `${window.location.origin}/#${path}`;
    const win = window.open(url, '_blank');
    if (!win) {
      window.location.replace(url);
    }
  }

  /** open window in new tab */
  openPrintPageInNewTab(path: string, id: any) {
    const url = `${window.location.origin}/#${path}`;
    const win = window.open(url, '_blank', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
    const isOilStickerImproved = this.dataService.isOilStickerImproved;
    const isOilStickerImprovedEnaled = id.innerHTML.search('isOilStickerImproved');

    // Your CSS as text
    win.document.write(`
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta http-equiv="X-UA-Compatible" content="IE=edge">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700;&display=swap" rel="stylesheet">
      <style type="text/css">
        ${isOilStickerImproved || isOilStickerImprovedEnaled > 1 ? isOilStickerImprovedstyles : styles}
      </style>
    </head>
    <body>
      ${id.innerHTML}
    </body>
    </html>
    `);
    win.document.close();
    win.setTimeout(function () {
      win.focus();
      win.print();
      win.close();
    }, 1500);

    if (!win) {
      window.location.replace(url);
    }
  }

  /** open window in same tab */
  openPageInSameTab(path: string, data = null) {
    // Setting error message into state
    const navigationExtras: NavigationExtras = { state: data };
    const url = `${window.location.origin}/#${path}`;
    const win = window.open(url, '_self');
    this.router.navigate([path], navigationExtras);
    if (!win) {
      window.location.replace(url);
    }
  }

  openPageInSameTabAngularRoute(path: string, data = null, queryParams: any = null) {
    // Setting error message into state
    const navigationExtras: NavigationExtras = { state: data, queryParams: queryParams };
    this.router.navigate([path], navigationExtras);
  }

  /** open window in existing tab */
  openPageInNewExistingTab(path: string, target: string) {
    const url = `${window.location.origin}/#${path}`;
    try {
      const win = window.open(url, target);

      if (!win) {
        window.location.replace(url);
      }
    } catch (err) {
      window.open(url, '_blank');
    }
  }

  openPage(path: string) {
    const url = `${window.location.origin}/#${path}`;
    const win = window.open(url, '_self');
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigate([path]));
    if (!win) {
      window.location.replace(url);
    }
  }

  deepCopy(data) {
    try {
      return JSON.parse(JSON.stringify(data));
    } catch (err) { }
  }

  estimateStaticTitle(title, estimate?) {
    if (title === null || title === '') {
      if (estimate && estimate.customer && estimate.customer.fullName) {
        return estimate.customer.fullName;
      } else {
        return config.defaultTitle;
      }
    } else {
      return title;
    }
  }

  purchseOrderStaticTitle(title, purchaseOrder?) {
    if (title === null || title === '') {
      if (purchaseOrder && purchaseOrder.customer && purchaseOrder.customer.fullName) {
        return purchaseOrder.customer.fullName;
      } else {
        return config.defaultTitle;
      }
    } else {
      return title;
    }
  }

  apptointmentStaticTitle(appt?: any) {
    if (appt && appt.customer && appt.customer.fullNameQBO) {
      return `${appt.customer.fullNameQBO}`;
    }
  }

  intersectionBy(source, select, key) {
    return intersectionBy(source, select, key);
  }

  addDuration(date, period, type) {
    return moment(date).add(period, type);
  }

  addOneBusinessDay(date?) {
    const dateTime = date ? date : this.todayDate();
    const day = moment(date).day();

    if (day === this.weekday.fri) {
      return moment(dateTime).add(3, 'd').startOf('hour').toDate();
    } else if (day === this.weekday.sat) {
      return moment(dateTime).add(2, 'd').startOf('hour').toDate();
    } else {
      return moment(dateTime).add(1, 'd').startOf('hour').toDate();
    }
  }

  convertHexToRGBA(hex, opacity) {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }

  parseDateForTimeZone(date) {
    return moment(date).format('YYYY-MM-DD HH:mm');
  }

  daysLabel(value) {
    return `${value > 1 ? 'days' : 'day'}`;
  }

  isPreviousDate(date) {
    if (date) {
      return moment(date).isBefore(moment().toDate());
    }
    return true;
  }

  toNumber(num) {
    return Number(num);
  }

  toCeil(num) {
    return Math.ceil(num);
  }

  isMobileOrTablet() {
    let check = false;
    (function (a) {
      // tslint:disable-next-line: max-line-length
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4),
        )
      ) {
        check = true;
      }
    })(navigator.userAgent || navigator.vendor);
    return check;
  }

  detectMob() {
    const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  removeTagsFromString(str) {
    if (str) {
      const convrtedString = str.replace(/<(.|\n)*?>/g, '');
      return convrtedString;
    }
  }
  getCurrentTimezoneDateTime() {
    const offset = parseInt(this.getCompany().timezone.replace('GMT', ''));

    // create Date object for current location
    const d = new Date();
    // convert to msec
    // subtract local time zone offset
    // get UTC time in msec
    const utc = d.getTime() + d.getTimezoneOffset() * 60000;
    // create new Date object for different city
    // using supplied offset
    const nd = new Date(utc + 3600000 * offset);

    return nd;
  }

  getRatingStar(value) {
    if (value) {
      // Filled star
      const filled = new Array(Math.floor(value)).fill('fas fa-star font-primary');

      // Half star
      const half = value - filled.length;
      if (value < 5) {
        if (half > 0.26 && half < 0.75) {
          filled.push('fas fa-star-half-alt font-primary');
        } else if (half < 0.26) {
          filled.push('fal fa-star font-gray');
        } else {
          filled.push('fas fa-star font-primary');
        }
      }

      // Empty Star
      const empty = new Array(5 - Math.floor(filled.length)).fill('fal fa-star font-gray');
      return [...filled, ...empty];
    } else {
      const empty = new Array(5).fill('fal fa-star font-gray');
      return [...empty];
    }
  }

  parseCurrencyTags(str: string) {
    let value = str;
    let startIndex = value.indexOf('<currency>');

    if (startIndex === -1) {
      return str;
    }
    value = value.replace('<currency>', '<currenct>');
    startIndex += 10;
    const endIndex = value.indexOf('<currency>');
    const amount = value.slice(startIndex, endIndex);

    return str.replace(`<currency>${amount}<currency>`, this.currencyPipe.transform(amount, currency.USD));
  }

  escapeRegex(string: string) {
    return string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  createPaymentLogTooltip(payment: any) {
    const history = payment.paymentHistory;
    const paymentObject = payment;
    if (history.length) {
      let tooltip = '';
      history.forEach(({ action, fullName, amount = 0, createdAt, paymentMethod, paymentType, resend }, index) => {
        if (action === 'depositRequest') {
          tooltip = this.createDepositTooltip({ fullName, createdAt, amount, resend });
          return tooltip;
        }
        tooltip = `

          <div class="tooltip-2">
              <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex payment-history-wrapper">
                <div class="payment-history font-800 font-gray-35 d-flex mb-0">
                  <i class="fal fa-plus-square align-self-start pt-1"></i>
                  <span class="payment-history-title">
                    <b class="font-600">${action === 'create' ? 'Added' : 'Modified'} by ${fullName}</b>
                  </span>
                </div>
                <div class="${paymentObject.isBulkPayment ? 'payment-history font-800 font-gray-35 d-flex' : 'd-none'} ">
                  <span class="payment-history-title ml-19">
                    <b class="font-600"> through bulk payment against ${paymentObject?.statement?.statementName || ''} statement</b>
                  </span>
                </div>
                <div class="payment-date ${payment.isCreditPayment && 'flex-between-center pt-1'}">
                  <span class="font-gray-35">
                    <i class="fal ${payment.isCreditPayment ? 'fa-paper-plane' : 'fa-calendar-alt'}"></i>
                    ${this.dateService.formatDate(
          payment.paymentDate ? new Date(payment.paymentDate) : this.dateService.getCurrentDateTimeTz(new Date(createdAt).getTime()),
          datetimeFormat.mmmdyyhhmmaa,
        )}</span>

                    <div class="${payment.isCreditPayment ? 'paid-amount' : 'd-none'}">
                    <span>$${this.formatAmount(amount.toFixed(2))}</span>
                  </div>
                </div>
                <div class="${!payment.isCreditPayment ? 'payment-amount' : 'd-none'}">
                  <div class="payment-mode w-75 font-primary">
                    <i class="fal pr-0 ${paymentMethod === 'cash' ? 'fa-money-bill' : paymentMethod === 'thirdParty' ? 'fa-store' : 'fa-credit-card'}"></i>
                    <span class="text-capitalize text-wrap">
                    ${this.createPaymentMethodText(payment, paymentMethod, paymentType)}

                    ${this.paymentToolTipTxt(payment, paymentMethod)}

                    </span>
                    </div>
                  <div class="paid-amount font-primary">
                    <span>$${this.formatAmount(amount.toFixed(2))}</span>
                  </div>
                </div>
                <div class="${action === 'create' ? 'd-none' : 'border-top my-2 d-inline-block w-100'}"></div>
              </div>
            </div>
          ${tooltip}`;
      });

      tooltip = `

      <div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom">
            <i class="fal fa-usd-circle"></i>${payment.isCreditPayment ? 'Credit applied' : 'Payment history'}
      </div>
      ${tooltip}
      </div>`;

      return tooltip;
    }

    return '';
  }

  createPaymentMethodText(payment, paymentMethod, paymentType) {
    if (payment.isPO) {
      return paymentType;
    }

    // if (paymentMethod === 'thirdParty') {
    //   return '3rd party';
    // }

    if (['writeOff', 'thirdParty'].includes(paymentMethod)) {
      return paymentType;
    }

    return paymentMethod === 'cheque' ? this.dataService.checkTxt : paymentMethod;
  }
  createDepositTooltip(data: any) {
    if (data) {
      const { fullName, createdAt, amount, resend } = data;
      let tooltip = '';
      tooltip = `

      <div class="tooltip-2">
          <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex payment-history-wrapper">
            <div class="payment-history font-800 font-gray-35 d-flex mb-0">
              <i class="fal fa-plus-square align-self-start pt-1"></i>
              <span class="payment-history-title">
                <b class="font-600">Request ${resend ? 're-sent' : 'created'} by ${fullName}</b>
              </span>
            </div>
            <div class="payment-amount">
              <div class="payment-mode">
                <i class="fal  fa-paper-plane align-self-start pt-1"></i>
                <span class="text-capitalize">
                ${this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(createdAt).getTime()), datetimeFormat.mmmdyyhhmmaa)}
                </span>
                </div>
              <div class="paid-amount">
                <span>${this.currencyPipe.transform(amount || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      ${tooltip}`;

      tooltip = `

      <div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom">
            <i class="fal fa-usd-circle"></i>Deposit request
      </div>
      ${tooltip}
      </div>`;
      return tooltip;
    }
    return '';
  }

  paymentToolTipTxt(payment, paymentMethod) {
    let text = '';
    // if (paymentMethod === 'cheque') text = `-${this.dataService.checkTxt}`;
    // else if (paymentMethod == 'cash') text = `-${this.dataService.cash}`;
    // else
    if (payment.isOnline) text = `(Online) ${payment.card4Digits ? payment.paymentMethod?.name + ' *' + payment.card4Digits : ''}`;
    else if (payment.isCardSwipe) text = `(Swipe) ${payment.card4Digits ? '*' + payment.card4Digits : ''}`;
    else if (payment.isOnlinePaymentFeatureEnable && paymentMethod == 'card') text = `(Manual) ${payment.card4Digits ? '*' + payment.card4Digits : ''}`;
    else if (!payment.isOnlinePaymentFeatureEnable && paymentMethod == 'card') text = ` ${payment.card4Digits ? '*' + payment.card4Digits : ''}`;
    if (payment.isDebitCardPayment) {
      text = '- Debit ' + text;
    }
    return text;
  }

  createRefundTooltip(refund: any, payment?: any) {
    if (refund) {
      let tooltip = '';

      tooltip = `<div class="tooltip-2">
      <div
        class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex payment-history-wrapper">
        <div class="payment-history font-800 font-gray-35 d-flex">
          <i class="fal fa-plus-square align-self-start pt-1"></i>
          <span class="payment-history-title">
            <b class="font-600">Added by ${refund.fullName}</b>
          </span>
          <span class="ml-auto font-400">$${refund.amount}</span>
        </div>
        <div class="payment-date">
          <span class="font-gray-35">
            <i class="fal fa-calendar-alt"></i>
            ${this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(refund.refundDate).getTime()), datetimeFormat.mmmdyyhhmmaa)}</span>
        </div>
        <div class="payment-amount">
        <div class="payment-mode">
        </div>
          <div class="paid-amount">
          </div>
        </div>
      </div>
    </div>
          ${tooltip}`;

      tooltip = `

      <div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom">
            <i class="fal fa-usd-circle"></i>${payment?.isVoidPayment ? 'Void' : 'Refund'} History
      </div>
      ${tooltip}
      </div>`;
      return tooltip;
    }

    return '';
  }

  redirectToPayrixPortal = (transactionDetail) => {
    if (transactionDetail && transactionDetail.terminal) {
      window.open(`${environment.payrix_base_url}/merchants/terminalTxns/${transactionDetail.id}`, '_blank');
    } else if (transactionDetail && transactionDetail.id) {
      window.open(`${environment.payrix_base_url}/payments/profile/${transactionDetail.id}/customer`, '_blank');
    }
  };
  getMappedAttribute(attributes: any) {
    const attrCode = {
      AxleTypes: 'AX',
      BedTypes: 'BD',
      Brakes: 'BR',
      BodyStyles: 'BS',
      CabTypes: 'CB',
      Countries: 'CO',
      DriveTypes: 'DT',
      Engines: 'EN',
      ManufactureBodyCodes: 'MB',
      SubModels: 'SM',
      Springs: 'SP',
      Steering: 'ST',
      Transmissions: 'TR',
      WheelBases: 'WB',
    };
    const attrId = {
      AxleTypes: 'AxleTypeID',
      BedTypes: 'BedTypeID',
      Brakes: 'BrakeID',
      BodyStyles: 'BodyStyleID',
      CabTypes: 'CabTypeID',
      Countries: 'CountryID',
      DriveTypes: 'DriveTypeID',
      Engines: 'EngineID',
      ManufactureBodyCodes: 'ManufactureBodyCodeID',
      SubModels: 'SubModelID',
      Springs: 'SpringID',
      Steering: 'SteeringID',
      Transmissions: 'TransmissionID',
      WheelBases: 'WheelBaseID',
    };
    const attrMap = {};
    try {
      Object.keys(attributes).forEach((attr) => {
        const codeKey = attrCode[attr];
        const idKey = attrId[attr];

        if (codeKey && idKey) {
          attrMap[codeKey] = {};
          attributes[attr].forEach((data: any) => {
            attrMap[codeKey][data[idKey]] = { ...data };
          });
        }
      });
    } catch (err) {
      return {};
    }

    return attrMap;
  }

  makeMotorServiceName(displayName: string, position: any, timedescription: string, qualifiers: any[], attrMapping: any, attributes: any, type: string) {
    let qDescription = '';
    let pDescription = '';
    let aDescription: any = '';
    let descriptions: any = [];
    const attrMap = this.getMappedAttribute(attributes);

    if (position && position.Name && position.Name !== 'N/A') {
      pDescription = position.Name;
    }

    if (qualifiers.length) {
      const qDescriptions = [];
      qualifiers.forEach(({ Description }) => {
        if (!!Description) {
          qDescriptions.push(Description);
        }
      });

      if (qDescriptions && qDescriptions.length) {
        qDescription = qDescriptions.join(', ');
      }
    }

    if (attrMapping && attrMapping.length) {
      aDescription = [];

      attrMapping.forEach((attr: any) => {
        if (attrMap[attr.Type] && attrMap[attr.Type][attr.ID]) {
          const data = attrMap[attr.Type][attr.ID];

          if (attr.Type === 'BS' && data) {
            const { DoorCount = '', Type = '' } = data;
            if (DoorCount && Type) {
              aDescription.push(`Body Style: ${DoorCount} Door - ${Type}`);
            } else if (DoorCount) {
              aDescription.push(`Body Style: ${DoorCount} Door`);
            } else {
              aDescription.push(`Body Style: ${Type}`);
            }
          }

          if (attr.Type === 'TR' && data) {
            const { ControlType = '', ManufacturerCode = '', Speed = '' } = data;

            if (ControlType && ManufacturerCode && Speed) {
              aDescription.push(`Transmission: ${ControlType} (${Speed} Speed, ${ManufacturerCode})`);
            } else {
              aDescription.push(`Transmission: ${ControlType} (${Speed ? `${Speed} Speed` : ManufacturerCode})`);
            }
          }

          if (attr.Type === 'BR' && data) {
            const { FrontType = '', RearType = '', ABS = '' } = data;

            if (FrontType || RearType || ABS) {
              aDescription.push(`Brake: ${[FrontType, RearType, ABS].filter((str) => str).join('/')}`);
            }
          }

          if (attr.Type === 'DT' && data) {
            const { Type = '' } = data;

            if (Type) {
              aDescription.push(`Drive Type: ${Type}`);
            }
          }
        }
      });

      if (aDescription.length) {
        aDescription = aDescription.join(' - ');
      }
    }

    if (timedescription && ['labor', 'service'].indexOf(type) !== -1) {
      descriptions.push(timedescription);
    }

    if (pDescription && ['service', 'labor', 'part'].indexOf(type) !== -1) {
      descriptions.push(pDescription);
    }

    if (qDescription && ['service', 'labor', 'part'].indexOf(type) !== -1) {
      descriptions.push(qDescription);
    }

    if (aDescription && ['labor', 'part', 'heading'].indexOf(type) !== -1) {
      descriptions.push(aDescription);
    }

    if (type === 'heading') {
      return descriptions.join(' - ');
    }

    if (type === 'labor' || type === 'service') {
      descriptions = descriptions.join(', ');

      if (descriptions) {
        return `${displayName} (${descriptions})`;
      } else {
        return `${displayName}`;
      }
    }
  }

  /**
   * Construct query params
   *
   * @param url url to append queries to
   * @param query query params object
   */
  constructQueryParams(url, query) {
    // start with request url as initial string
    let updatedUrl = url;

    // only run query construction if query object is not null or has keys
    if (query && Object.keys(query).length) {
      // start query string
      updatedUrl = updatedUrl + '?';

      // iterate each keys in query string
      Object.keys(query).forEach((key, index) => {
        // check if value against current key is array or not
        if (Array.isArray(query[key])) {
          // if value is array loop through all values in array for construction
          query[key].forEach((val, nIndex) => {
            // only add & seperator if not first key in query or not first value in query
            const andSeperator = index !== 0 || nIndex !== 0 ? '&' : '';

            // append query key value in query params
            updatedUrl = `${updatedUrl}${andSeperator}${key}=${val}`;
          });
        } else {
          // only add & seperator if not first key in query
          const andSeperator = index !== 0 ? '&' : '';

          // append query key value in query params
          updatedUrl = `${updatedUrl}${andSeperator}${key}=${![null, undefined].includes(query[key]) ? query[key] : ''}`;
        }
      });
    }

    // return url
    return updatedUrl;
  }

  setVehicleDisabledFields(isMotorConnected: boolean, isCarfaxConnected: boolean, connectedVia: string) {
    const disabled: any = {};

    if (isMotorConnected || isCarfaxConnected) {
      disabled.year = true;
      disabled.make = true;
      disabled.model = true;
      disabled.engine = true;
    }

    if (connectedVia === 'license') {
      disabled.licensePlateNo = true;
      disabled.vin = true;
      disabled.state = true;
    } else if (connectedVia === 'vin') {
      disabled.vin = true;
    } else {
      disabled.licensePlateNo = false;
      disabled.vin = false;
      disabled.state = false;
    }

    if (isCarfaxConnected) {
      disabled.vin = true;
    }

    return disabled;
  }

  parseTireAttr(str: string) {
    // Numbers RegEx
    const numberRegExp = /(\d+(\.\d+)?)/;

    const [, value] = numberRegExp.exec(str) || ['0', '0'];

    return `${value}` || '0';
  }

  tireSizeParser(sizeStr: string = '') {
    try {
      const format1RegExp = /^\s*(\w*\d{1,3}(?:\.\d)?)\s*(?:[-/X](\d{1,2}(?:\.\d)?)?)?\s*(\w*[RBD])\s*(\d{1,2}(?:\.\d)?)/;
      const format2RegExp = /(\w+)\s*x\s*(\w+)\s*x\s*(\w+)/;
      const format3RegExp = /^(\d{3})(\d{2})(\d{2})$/;
      const format4RegExp = /^\s*(\w*\d{1,3}(?:\.\d)?)\s*(?:[-/X](\d{1,2}(?:\.\d)?)?)?\s*(\w*[-])\s*(\d{1,2}(?:\.\d)?)/;
      const format5RegExp = /^\s*(\w*\d{1,3}(?:\.\d)?)\s*(?:[-/X](\d{1,2}(?:\.\d)?)?)?\s*(?:[-/X])\s*(\d{1,2}(?:\.\d)?)$/;

      const tireUpdate = {
        tireSize: null,
        tireWidth: null,
        tireProfile: null,
        tireSizeSearch: null,
        label: sizeStr,
      };

      if (format1RegExp.test(sizeStr)) {
        const result = format1RegExp.exec(sizeStr);
        const [match, width = '', profile = '', radial, size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = profile ? this.parseTireAttr(profile) : '0'; // Set profile to 0 if missing
        tireUpdate.tireSize = this.parseTireAttr(size);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      } else if (format2RegExp.test(sizeStr)) {
        const result = format2RegExp.exec(sizeStr);
        const [match, width = '', profile = '', size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = this.parseTireAttr(profile);
        tireUpdate.tireSize = this.parseTireAttr(size);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      } else if (format3RegExp.test(sizeStr)) {
        const result = format3RegExp.exec(sizeStr);
        const [match, width = '', profile = '', size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = this.parseTireAttr(profile);
        tireUpdate.tireSize = this.parseTireAttr(size);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      } else if (format4RegExp.test(sizeStr)) {
        const result = format4RegExp.exec(sizeStr);
        const [match, width = '', profile = '', radial, size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = profile ? this.parseTireAttr(profile) : '0'; // Set profile to 0 if missing
        tireUpdate.tireSize = this.parseTireAttr(size);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      } else if (format5RegExp.test(sizeStr)) {
        const result = format5RegExp.exec(sizeStr);
        const [match, width = '', profile = '', radial, size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = profile ? this.parseTireAttr(profile) : '0'; // Set profile to 0 if missing
        tireUpdate.tireSize = this.parseTireAttr(radial);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      } else {
        sizeStr = sizeStr.replace(' ', '');
        sizeStr = sizeStr.replace(/\D/g, '');
        const result = format3RegExp.exec(sizeStr);
        const [match, width = '', profile = '', size = ''] = result;

        tireUpdate.tireWidth = this.parseTireAttr(width);
        tireUpdate.tireProfile = this.parseTireAttr(profile);
        tireUpdate.tireSize = this.parseTireAttr(size);

        tireUpdate.tireSizeSearch = `${this.trimString(tireUpdate.tireWidth)}${this.trimString(tireUpdate.tireProfile)}${this.trimString(tireUpdate.tireSize)}`;
      }

      return tireUpdate;
    } catch (err) {
      console.log('tire parser error', err);
      return {
        tireSize: null,
        tireWidth: null,
        tireProfile: null,
        tireSizeSearch: null,
        label: sizeStr,
      };
    }
  }

  setVehicleConnectedMotorData(vehicle: any, searchType: string, query: string) {
    const data: any = {};

    if (vehicle && vehicle.Year) {
      data.year = Number(vehicle.Year);
      data.make = vehicle.MakeName;
      data.model = `${vehicle.ModelName} ${vehicle.SubModelName || ''}`;
      data.engine = vehicle.EngineDescription;
      data.title = `${vehicle.Year} ${vehicle.MakeName} ${vehicle.ModelName} ${vehicle.SubModelName} ${vehicle.EngineDescription}`;
      data.name = `${data.year || ''} ${data.make || ''} ${data.model || ''}`;
      if (vehicle?.DriveType)
        data.driveType = {
          name: vehicle.DriveType,
        };
      if (vehicle?.TransmissionType)
        data.transmission = vehicle.TransmissionType.toLowerCase().includes('automatic')
          ? {
              label: vehicle.TransmissionType,
              value: 'automatic',
            }
          : {
              label: vehicle.TransmissionType,
              value: 'manual',
            };
      if (vehicle.licensePlateNo) {
        data.connectedVia = 'license';
        data.licensePlateNo = vehicle.licensePlateNo.toUpperCase();
      }

      if (vehicle.VIN) {
        if (!data.connectedVia) {
          data.connectedVia = 'vin';
        }

        data.vin = vehicle.VIN;
      }

      if (vehicle.BaseVehicleID) {
        data.apiResponse = vehicle;
      }

      if (searchType === 'VIN' && !data.vin) {
        data.vin = query;
      }
    }

    if (searchType === 'carfax') {
      data.isCarfaxConnected = true;
    } else if (!!vehicle) {
      data.isMotorConnected = true;
    } else {
      data.isCarfaxConnected = false;
      data.isMotorConnected = false;
    }

    return data;
  }

  setVehicleMotorSearchResults(vehicle: any, query: string) {
    const regex = new RegExp(`(${query})`, 'ig');

    vehicle.htmlName = `${vehicle.Year} ${vehicle.MakeName || vehicle.Make || ''} ${vehicle.ModelName || vehicle.Model || ''} ${vehicle?.SubModelName || vehicle?.SubModel || ''}`;
    vehicle.name = vehicle.htmlName;
    vehicle.htmlName = vehicle.htmlName.replace(regex, `<span class="font-600"><b>$1</b></span>`);

    if (vehicle.licensePlateNo) {
      vehicle.htmlLicensePlate = vehicle.licensePlateNo.replace(regex, `<span class="font-600"><b>$1</b></span>`);
    }

    if (vehicle.VIN) {
      vehicle.htmlVin = 'VIN: ' + vehicle.VIN.replace(regex, `<span class="font-600"><b>$1</b></span>`);
    }

    if (vehicle.EngineDescription) {
      vehicle.htmlEngine = `Engine${vehicle?.isOldGen ? '*' : ''}: ` + vehicle.EngineDescription.replace(regex, `<span class="font-600"><b>$1</b></span>`);
    }

    return vehicle;
  }

  renderItemQuantity(item: any) {
    const { type, count, unitOfMeasuring } = item;

    if (type === 'labor') {
      return `${count || 0} hour${count > 1 ? 's' : ''}`;
    }

    if (type === 'part') {
      return `${count || 0} unit${count > 1 ? 's' : ''}`;
    }

    if (type === 'parts') {
      return `${count || 0} unit${count > 1 ? 's' : ''}`;
    }

    if (type === 'fluids') {
      return `${count || 0} ${units.fluids[unitOfMeasuring] || 'units'}`;
    }

    if (type === 'tire') {
      return `${count || 0} tire${count > 1 ? 's' : ''}`;
    }

    if (type === 'other') {
      return `${count || 0} item${count > 1 ? 's' : ''}`;
    }

    if (type === 'sublet') {
      return `${count || 0} item${count > 1 ? 's' : ''}`;
    }

    if (type === 'bundle') {
      return `${count || 0} unit${count > 1 ? 's' : ''}`;
    }
  }

  highlighedItemType(text: string, query: string) {
    if (text && query) {
      let str = text.replace(/\s/g, '&nbsp;');

      const regex = new RegExp(`(${query.replace(/\s/g, '&nbsp;')})`, 'ig');
      str = str.replace(regex, `<span class="font-600"><b>$1</b></span>`);

      return str;
    }

    return text;
  }

  formatAmount(value: any) {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  formatRegNumber(str) {
    if (str) {
      //   const value = str;
      //   const obj: any = {};
      //   obj.title = value.split(':')[0];
      //   obj.value = value.split(':')[1];
      //   obj.value = obj.value.replace(new RegExp('\r?\n', 'g'), '<br> ');

      //   const inner_HTML = `<div class="table">
      //   <div class="flex-center-start">
      //     <div class="td">${obj.title}:</div>
      //     <div class="td reg-value-settig">${obj.value}</div>
      //   </div>
      // </div>`;
      // return inner_HTML;
      return str;
    }
  }

  createContactTooltip(recipients: any, title: string, isReminder: boolean = false) {
    const unsubscribeCount = { email: 0, phone: 0 };

    recipients.emails.forEach(({ type, isOperationalNotifEnabled }) => {
      unsubscribeCount.email += isOperationalNotifEnabled ? 0 : 1;
    });
    recipients.phones.forEach(({ type, isOperationalNotifEnabled }) => {
      unsubscribeCount.phone += isOperationalNotifEnabled ? 0 : 1;
    });

    let tooltip = `
    <div class="tooltip-2">
      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="tech-service font-800 font-gray-35 d-flex mb-1">
          <span class="tech-service-title font-secondary-900">
            Email
          </span>
        </div>
        ${recipients.emails
        .map(
          ({ value }) =>
            `<div class="apt-tooltip-row text-right font-gray-55">
                <i class="fal fa-envelope align-self-center"></i>
                <span class="font-12 font-gray-35 truncate d-inline-block text-left" style="width: 208px">${value}</span>
              </div>`,
        )
        .join('')}
      ${isReminder && unsubscribeCount.email
        ? `<div class="apt-tooltip-row text-right">
                <span class="font-12 font-yellow truncate d-inline-block text-left" style="width: 208px">
                  ${unsubscribeCount.email} email${unsubscribeCount.email > 1 ? 's' : ''} opted out
                </span>
              </div>`
        : ''
      }
      </div>
    </div>

    <div class="tooltip-2 mt-1 apt-contact-number">
      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="tech-service font-800 font-gray-35 d-flex mb-1">
          <span class="tech-service-title font-secondary-900">
            Phone
          </span>
        </div>

        ${recipients.phones
        .map(
          ({ value, type }) =>
            `<div class="apt-tooltip-row text-right font-gray-55">
                <i class="fal fa-mobile align-self-center"></i>
                <span class="font-12 font-gray-35 apt-contanct-number">
                  ${this.formatPhoneNumber(value)}
                </span>
                <span class="pl-2 font-gray-55">
                  ${type === 'mobile' ? 'Mobile' : 'Landline'}
                </span>
              </div>`,
        )
        .join('')}
      ${isReminder && unsubscribeCount.phone
        ? `<div class="apt-tooltip-row text-right">
                <span class="font-12 font-yellow truncate d-inline-block text-left" style="width: 208px">
                  ${unsubscribeCount.phone} phone number opted out
                </span>
              </div>`
        : ''
      }

      </div>
    </div>
    `;

    return `
      <div class="tooltip-seondary">
        <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom">
          <i class="fal fa-check"></i>${title}
        </div>
        ${tooltip}
      </div>`;
  }

  isAppleDevice() {
    try {
      return ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform);
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  isIpad() {
    try {
      return ['iPad Simulator', 'iPad'].includes(navigator.platform);
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  mktTemplateParser(data: any, text: string) {
    let content = text;

    if (content) {
      if (data.sendEstimate) {
        content = content.replace(new RegExp(this.escapeRegex('[estimate_send_date]'), 'g'), this.dateService.formatDate(data.sendEstimate, datetimeFormat.mmmdyyhhmmaa) || '');
      }

      if (data.vehicleName) {
        content = content.replace(new RegExp(this.escapeRegex('[vehicle_ymm]'), 'g'), data.vehicleName || '');
      }

      if (data.code) {
        content = content.replace(new RegExp(this.escapeRegex('[ro_code]'), 'g'), data.code || '');
      }

      if (data.title) {
        content = content.replace(new RegExp(this.escapeRegex('[ro_name]'), 'g'), data.title || '');
      }

      if (data.invoiceDate) {
        content = content.replace(new RegExp(this.escapeRegex('[ro_invoice_date]'), 'g'), this.dateService.formatDate(data.invoiceDate, datetimeFormat.mmmdyyyy));
      }

      if (data.customerCreatedAt) {
        content = content.replace(new RegExp(this.escapeRegex('[customer_creation_date]'), 'g'), this.dateService.formatDate(data.customerCreatedAt, datetimeFormat.mmmdyyyy));
      }

      if (data.vehicleCreatedAt) {
        content = content.replace(new RegExp(this.escapeRegex('[vehicle_creation_date]'), 'g'), this.dateService.formatDate(data.vehicleCreatedAt, datetimeFormat.mmmdyyyy));
      }

      if (data.firstName) {
        content = content.replace(new RegExp(this.escapeRegex('[[CUSTOMER_FIRST_NAME]]'), 'g'), data.firstName || '');
      }

      if (data.shopName) {
        content = content.replace(new RegExp(this.escapeRegex('[[SHOP_NAME]]'), 'g'), data.shopName || '');
      }

      if (data.shopContact) {
        content = content.replace(new RegExp(this.escapeRegex('[[SHOP_CONTACT_NUMBER]]'), 'g'), data.shopContact || '');
      }

      if (data.lastName) {
        content = content.replace(new RegExp(this.escapeRegex('[[CUSTOMER_LAST_NAME]]'), 'g'), data.lastName || '');
      }
    }

    return content;
  }

  unsubscriptionReminderTooltip(contact: any) {
    let contactCount = 0;
    let unsubscriptionCount = 0;

    if (contact.emails) {
      contactCount += contact.emails.length;
      unsubscriptionCount += contact.emails.filter(({ isOperationalNotifEnabled }) => isOperationalNotifEnabled === false).length;
    }

    if (contact.phones) {
      contactCount += contact.phones.length;
      unsubscriptionCount += contact.phones.filter(({ isOperationalNotifEnabled }) => isOperationalNotifEnabled === false).length;
    }
    if (unsubscriptionCount) {
      return `${unsubscriptionCount} out of ${contactCount} contacts are unsubscribed from receiving reminders. Click <i class="fal fa-edit"></i> to change preferences`;
    } else {
      return '';
    }
  }

  onKeyPress(obj: AllowDecimalMask, allowNegative: boolean = false): boolean {
    let charCode = obj.event.which ? obj.event.which : obj.event.keyCode;
    if (
      charCode != 46 &&
      charCode > 31 &&
      (charCode < 48 || charCode > 57) &&
      !(
        allowNegative &&
        charCode == 45 &&
        (('' + obj.ngModelValue).length == 1 || (obj.inputControl.nativeElement.selectionStart == 0 && obj.inputControl.nativeElement.selectionEnd == ('' + obj.ngModelValue).length))
      )
    ) {
      obj.event.preventDefault();
      return false;
    } else if (charCode == 46 && obj.ngModelValue) {
      if (('' + obj.ngModelValue).split('.').length >= 2) {
        if (obj.inputControl.nativeElement.selectionStart == 0 && obj.inputControl.nativeElement.selectionEnd == ('' + obj.ngModelValue).length) {
          obj.ngModelValue = obj.event.key;
          obj.isChanged = true;
          return true;
        }
        obj.event.preventDefault();
        return false;
      }
    } else if (('' + obj.ngModelValue).indexOf('.') >= 0) {
      if (('' + obj.ngModelValue).substring(('' + obj.ngModelValue).indexOf('.')).length >= 3) {
        if (obj.inputControl.nativeElement.selectionStart == 0 && obj.inputControl.nativeElement.selectionEnd == ('' + obj.ngModelValue).length) {
          obj.ngModelValue = obj.event.key;
          obj.isChanged = true;
          return true;
        }
        obj.event.preventDefault();
        return false;
      }
    }
    return true;
  }

  validatePaste(obj: AllowDecimalMask) {
    let regex = new RegExp('^[0-9.]*$');
    let copiedText: string = obj.ngModelValue + obj.event.clipboardData.getData('text');
    if (obj.inputControl.nativeElement.selectionStart == 0 && obj.inputControl.nativeElement.selectionEnd == ('' + obj.ngModelValue).length) {
      copiedText = obj.event.clipboardData.getData('text');
    }
    let index = ('' + copiedText).indexOf('.');
    if (!regex.test(copiedText)) {
      obj.event.preventDefault();
      return false;
    } else if (index >= 0) {
      if (('' + copiedText).split('.').length > 2) {
        obj.event.preventDefault();
        return false;
      } else if (('' + copiedText).substring(index).length > 3) {
        obj.ngModelValue = ('' + copiedText).substring(0, index) + ('' + copiedText).substring(index, index + 3);
        obj.isChanged = true;
        return true;
      }
    }
    return true;
  }

  getChequeText(country: string) {
    if (country === 'Canada') return 'Cheque';
    else return 'Check';
  }

  getPayrixPaymentStatus(status: number) {
    const allStatus = {
      0: 'In Progress',
      1: 'Approved',
      2: 'Failed',
      3: 'Captured',
      4: 'Settled',
      5: 'Returned',
    };
    return allStatus[status];
  }

  isMileageVinRequired = ({ enableMandatoryVin, isMileageMandatory, VIN, mileage, isTrackMileageInAndOut, mileageOut }) => {
    const data = {
      isVinRequired: false,
      isMileageRequired: false,
      isMileageOutRequired: false,
    };

    if (enableMandatoryVin && !VIN) {
      data.isVinRequired = true;
    }

    if (isMileageMandatory && !mileage) {
      data.isMileageRequired = true;
    }

    if (isTrackMileageInAndOut && !mileageOut) {
      data.isMileageOutRequired = true;
    }

    return data;
  };

  isCurrentEmployeeRoRestricted(restrictedEmployeeId: string) {
    const { employeeId } = this.getCompany();
    if (restrictedEmployeeId && employeeId === restrictedEmployeeId) {
      return true;
    }
    return false;
  }

  getOrderRestrictionStatus(restriction: any = {}): string {
    const { token, status = '' } = restriction;

    if (!status || status === 'readOnly') {
      return 'readOnly';
    } else if (status === 'restricted') {
      if (this.isLockOrderSessionExist(token)) {
        return 'editable';
      } else {
        return 'restricted';
      }
    }
  }

  formatEmployeeName(firstName: string = '', lastName: string = '') {
    let employeeName = '';

    employeeName = firstName.length > 8 ? `${firstName.slice(0, 8)}...` : firstName;
    employeeName += ` ${lastName.slice(0, 1)}.`;

    return employeeName;
  }

  setSession(key: string, value: string) {
    sessionStorage.setItem(key, value);
  }

  makeOrderSessionToken(orderId: string, browserSessionId: any) {
    const { employeeId } = this.getCompany();

    return `${orderId}-${employeeId}-${browserSessionId}`;
  }

  setLockOrderSession(value: string) {
    const key = 'LOCK_SESSIONS';
    let sessions = this.getSessionByKey(key);

    if (!sessions) {
      sessions = [];
    }

    sessions.push(value);
    sessionStorage.setItem(key, JSON.stringify(sessions));
  }

  isLockOrderSessionExist(sessionToken: string) {
    const key = 'LOCK_SESSIONS';
    let sessions = this.getSessionByKey(key);

    if (sessions && sessions.length) {
      return sessions.includes(sessionToken);
    }

    return false;
  }

  getOrderSessions(orderId: string) {
    const key = 'LOCK_SESSIONS';
    const sessions = this.getSessionByKey(key);

    if (sessions && sessions.length) {
      return sessions.filter((token: string = '') => token && token.startsWith(orderId));
    }

    return [];
  }

  getSessionByKey(key: string) {
    try {
      return JSON.parse(sessionStorage.getItem(key));
    } catch (err) {
      console.log('err in getting session by key ', err);
      return null;
    }
  }

  getLockOrderSessions() {
    try {
      const key = 'LOCK_SESSIONS';
      this.getSessionByKey(key);
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  shouldDisplayCommissionPlan(): boolean {
    return this.dataService.isCommissionPlanEnabled && (this.dataService.canViewEditCommission || this.dataService.isSupportAdminAccount);
  }

  getCartGroupTaxSettings(source: string, repairOrder: any, lastUpdatedCartGroupIndex: number = 0): Record<string, any> {
    const quoteMaps = {
      partsTech: repairOrder?.partsTechQoute?.taxSettings ||
        (repairOrder?.oldPartsTechQoutes?.length
          ? repairOrder?.oldPartsTechQoutes?.[lastUpdatedCartGroupIndex]?.partsTechQoute?.taxSettings
          : {}),

      nexpart: repairOrder?.nexpartQoute?.quote?.taxSettings ||
        repairOrder?.oldNexpartQoute?.[lastUpdatedCartGroupIndex]?.quote?.taxSettings,

      speedDial: repairOrder?.speedDialQoute?.quote?.taxSettings ||
        repairOrder?.oldSpeedDialQoute?.[lastUpdatedCartGroupIndex]?.quote?.taxSettings,

      // manualOrder: repairOrder?.manualQoute?.taxSettings ||
      //   repairOrder?.oldManualQoute?.[lastUpdatedCartGroupIndex]?.taxSettings,

      tireHub: repairOrder?.tireHubQoute?.quote?.taxSettings ||
        repairOrder?.oldTireHubQoute?.[lastUpdatedCartGroupIndex]?.quote?.taxSettings,

      tireConnect: repairOrder?.tireConnectQoute?.quote?.taxSettings ||
        repairOrder?.oldTireConnectQoute?.[lastUpdatedCartGroupIndex]?.quote?.taxSettings,

      repairLink: repairOrder?.repairLinkQoute?.quote?.taxSettings ||
        repairOrder?.oldRepairLinkQoute?.[lastUpdatedCartGroupIndex]?.quote?.taxSettings,
    };

    return quoteMaps[source] || {};
  }

  getCartGroupTaxSettingsByGroupId(source: string, repairOrder: any, cartGroupId: string): any {
    const normalizedSource = source.toLowerCase();

    let taxMap = {};

    switch (normalizedSource) {
      case 'partstech':
        taxMap =
          repairOrder.partsTechQoute?.quote?.taxSettings ||
          (Array.isArray(repairOrder.oldPartsTechQoutes)
            ? repairOrder.oldPartsTechQoutes.find(q => q.partsTechQoute?.taxSettings?.[cartGroupId])?.partsTechQoute?.taxSettings
            : {});
        break;

      case 'nexpart':
        taxMap =
          repairOrder.nexpartQoute?.quote?.taxSettings ||
          (Array.isArray(repairOrder.oldNexpartQoute)
            ? repairOrder.oldNexpartQoute.find(q => q.quote?.taxSettings?.[cartGroupId])?.quote?.taxSettings
            : {});
        break;

      case 'tirehub':
        taxMap =
          repairOrder.tireHubQoute?.quote?.taxSettings ||
          (Array.isArray(repairOrder.oldTireHubQoute)
            ? repairOrder.oldTireHubQoute.find(q => q.quote?.taxSettings?.[cartGroupId])?.quote?.taxSettings
            : {});
        break;

      case 'tireconnect':
        taxMap =
          repairOrder.tireConnectQoute?.quote?.taxSettings ||
          (Array.isArray(repairOrder.oldTireConnectQoute)
            ? repairOrder.oldTireConnectQoute.find(q => q.quote?.taxSettings?.[cartGroupId])?.quote?.taxSettings
            : {});
        break;

      case 'repairlink':
        taxMap =
          repairOrder.repairLinkQoute?.quote?.taxSettings ||
          (Array.isArray(repairOrder.oldRepairLinkQoute)
            ? repairOrder.oldRepairLinkQoute.find(q => q.quote?.taxSettings?.[cartGroupId])?.quote?.taxSettings
            : {});
        break;

      case 'speeddial':
        taxMap = repairOrder.speedDialQoute?.quote?.taxSettings || {};
        break;

      // case 'manualorder':
      //   taxMap = repairOrder.manualQoute?.taxSettings || {};
      //   break;

      default:
        taxMap = {};
    }

    return taxMap?.[cartGroupId] || null;
  }




  formatOrderingQoutes(order: any) {
    const manualQoutes = [];
    const nexpartQoutes = [];
    const partsTechQoutes = [];
    const tireHubQoutes = [];
    const tireConnectQoutes = [];
    const repairLinkQoutes = [];
    const speedDialQoutes = [];
    const {
      // PartsTech
      partsTechSessionId,
      partsTechQoute,
      partsTechLastSynced,
      partsTechOrderPlacedAt,
      quoteCreatedAt,
      isOrderPlaced,
      isOrderDelivered,
      isOrderCancelled,
      isPartialDelivered,
      oldPartsTechQoutes,
      // Manual Order
      manualQoute,
      oldManualQoute,
      // Nexpart
      nexpartQoute,
      oldNexpartQoute,
      // TireHub
      tireHubQoute,
      oldTireHubQoute,
      // TireConnect
      tireConnectQoute,
      oldTireConnectQoute,
      // RepairLink
      repairLinkQoute,
      oldRepairLinkQoute,
      // Nexpart
      speedDialQoute,
      oldSpeedDialQoute,
    } = order;

    // PartsTech Qoutes
    if (partsTechQoute && partsTechQoute.sessionId) {
      partsTechQoutes.push({
        partsTechSessionId,
        partsTechQoute,
        partsTechLastSynced,
        partsTechOrderPlacedAt,
        quoteCreatedAt,
        isOrderPlaced,
        isOrderDelivered,
        isPartialDelivered,
        isOrderCancelled,
      });
    }

    if (oldPartsTechQoutes && oldPartsTechQoutes.length) {
      partsTechQoutes.push(...oldPartsTechQoutes);
    }

    // Nexpart Qoutes
    if (nexpartQoute && nexpartQoute.sessionId) {
      nexpartQoutes.push(nexpartQoute);
    }

    if (oldNexpartQoute && oldNexpartQoute.length) {
      nexpartQoutes.push(...oldNexpartQoute);
    }

    // SpeedDia; Qoutes
    if (speedDialQoute && speedDialQoute.internalReferenceId) {
      speedDialQoutes.push(speedDialQoute);
    }

    if (oldSpeedDialQoute && oldSpeedDialQoute.length) {
      speedDialQoutes.push(...oldSpeedDialQoute);
    }

    // ManualOrder Qoutes
    if (manualQoute && manualQoute.qouteId) {
      manualQoutes.push(manualQoute);
    }

    if (oldManualQoute && oldManualQoute.length) {
      manualQoutes.push(...oldManualQoute);
    }

    // TireHub Qoutes
    if (tireHubQoute && (tireHubQoute.qouteId || tireHubQoute.sessionId)) {
      tireHubQoutes.push(tireHubQoute);
    }

    if (oldTireHubQoute && oldTireHubQoute.length) {
      tireHubQoutes.push(...oldTireHubQoute);
    }

    // TireConnect Qoutes
    if (tireConnectQoute && (tireConnectQoute.qouteId || tireConnectQoute.sessionId)) {
      tireConnectQoutes.push(tireConnectQoute);
    }

    if (oldTireConnectQoute && oldTireConnectQoute.length) {
      tireConnectQoutes.push(...oldTireConnectQoute);
    }

    // RepairLink Qoutes
    if (repairLinkQoute && (repairLinkQoute.qouteId || repairLinkQoute.sessionId)) {
      repairLinkQoutes.push(repairLinkQoute);
    }

    if (oldRepairLinkQoute && oldRepairLinkQoute.length) {
      repairLinkQoutes.push(...oldRepairLinkQoute);
    }

    return { partsTechQoutes, nexpartQoutes, manualQoutes, tireHubQoutes, tireConnectQoutes, repairLinkQoutes, speedDialQoutes };
  }

  formatCartOrderItems(type: string, orders: any, sessionId?: string, orderingType?: string) {
    const orderDetails = [];
    if (type === 'partsTech') {
      if (orderingType === 'purchaseOrder') {
        orders.forEach(({ _id, type, supplier, items, fees = [], discounts = [], pOStatus }) => {
          const orderDetail: any = {};

          let totalCancelledItemAmount = 0;
          orderDetail.supplier = supplier?.name;
          let totalExportedItems = 0;
          let totalCoreCost = 0;
          orderDetail.items = items.map(
            ({ orderItemId, exportedDate, isExportedToAP, title, price, cost, core, quantity, deliveryDate, itemStatus, partName, description, partNumber, inventoryItem, subTotal, code, isTaxable, type }) => {
              totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
              if (itemStatus && itemStatus === 'cancelled') {
                totalCancelledItemAmount += (Number(cost) + Number(core)) * Number(quantity);
              }

              if (core) {
                totalCoreCost += Number(core) * Number(quantity);
              }
              const item = {
                orderItemId,
                title: title,
                image: '',
                price: price,
                quantity,
                cost: cost,
                core: core,
                actualCost: cost,
                actualCore: core,
                total: 0,
                exportedDate,
                isExportedToAP,
                description: description ?? partName,
                partNumber: partNumber || code,
                defaultItemStatus: itemStatus,
                isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
                deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
                itemStatus: (itemStatus || pOStatus) ?? 'received',
                displayItemStatus: itemStatus || pOStatus ? { label: orderStatusLabel[itemStatus || pOStatus] } : { label: 'Delivered' },
                inventoryItem: inventoryItem,
                subTotal: subTotal,
                type,
                isTaxable: isTaxable,
                _id,
              };

              console.log('itemStatus', itemStatus);

              item.total = cost * item.quantity;
              return item;
            },
          );
          orderDetail.fees = fees;
          orderDetail.discounts = discounts;
          orderDetail.totalExportedItems = totalExportedItems;
          orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;
          orderDetail.totalCoreCost = totalCoreCost;
          orderDetails.push(orderDetail);
        });
      } else {
        orders.forEach(({ supplier, parts, fees = [], discounts = [] }) => {
          const orderDetail: any = {};
          let totalExportedItems = 0;
          let totalCoreCost = 0;

          orderDetail.supplier = supplier?.name;
          orderDetail.items = parts.map(({ isExportedToAP, exportedDate, partId, orderItemId, partName, imageUrl, price, quantity, description, partNumber, itemStatus, deliveryDate, isTaxable }) => {
            totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
            totalCoreCost += price.core * quantity;
            const item = {
              partId,
              orderItemId,
              title: partName,
              image: imageUrl,
              price: price?.list,
              quantity: quantity,
              cost: price?.cost,
              core: price?.core,
              actualCost: price?.cost,
              actualCore: price?.core,
              isExportedToAP,
              exportedDate,
              description: description ?? partName,
              partNumber,
              total: (price?.cost + price?.core) * quantity,
              isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
              deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
              itemStatus: itemStatus ?? '',
              displayItemStatus: itemStatus ? { label: orderStatusLabel[itemStatus] } : { label: 'Delivered' },
              isTaxable: isTaxable,
            };

            if (!itemStatus && deliveryDate) {
              item.itemStatus = 'received';
            }

            return item;
          });
          orderDetail.fees = fees;
          orderDetail.discounts = discounts;
          orderDetail.totalExportedItems = totalExportedItems;
          orderDetail.totalCoreCost = totalCoreCost;

          orderDetails.push(orderDetail);
        });
      }
    } else if (type === 'nextPart') {
      orders.forEach(({ Name, SellerId, BranchId, Parts: { Part = [] } }) => {
        const orderDetail: any = {};

        orderDetail.supplier = Name;
        let totalExportedItems = 0;
        let totalCancelledItemAmount = 0;
        let totalCoreCost = 0;
        orderDetail.items = Part.map(({ PartNumber, exportedDate, isExportedToAP, LineCode, List, Mdse, OrderQty, SellerPartDesc, CatalogPartDesc, deliveryDate, Core, itemStatus, isTaxable }) => {
          totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;

          totalCoreCost += Number(Core) * Number(OrderQty);

          if (itemStatus === 'cancelled') {
            totalCancelledItemAmount += (Number(Mdse) + Number(Core)) * Number(OrderQty);
          }
          const item = {
            orderItemId: `${sessionId}-${SellerId}-${BranchId}-${LineCode}-${PartNumber}`,
            title: SellerPartDesc,
            image: '',
            partNumber: PartNumber,
            description: CatalogPartDesc,
            price: Number(List),
            cost: Number(Mdse),
            core: Number(Core),
            actualCost: Number(Mdse),
            actualCore: Number(Core),
            quantity: Number(OrderQty),
            exportedDate,
            isExportedToAP,
            isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
            total: (Number(Mdse) + Number(Core)) * Number(OrderQty),
            deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
            displayItemStatus: itemStatus ? { label: orderStatusLabel[itemStatus] } : { label: 'Delivered' },
            itemStatus: itemStatus ?? '',
            isTaxable: isTaxable,
          };

          if (!itemStatus && deliveryDate) {
            item.itemStatus = 'received';
          }

          return item;
        });
        orderDetail.totalExportedItems = totalExportedItems;
        orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;
        orderDetail.totalCoreCost = totalCoreCost;
        orderDetails.push(orderDetail);
      });
    } else if (type === 'tireHub') {
      orders.forEach(({ orderNo, warehouse, items }) => {
        const orderDetail: any = {};
        let totalExportedItems = 0;
        let totalCancelledItemAmount = 0;
        let totalCoreCost = 0;

        orderDetail.orderNo = orderNo;
        orderDetail.supplier = `${warehouse?.name} ${orderNo && `(${orderNo})`}`;

        orderDetail.items = items.map(({ exportedDate, isExportedToAP, core, partId, orderItemId, title, image, cost, price, count, itemStatus, deliveryDate, description, partNumber, isTaxable }) => {
          totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
          totalCoreCost += core;
          if (itemStatus && itemStatus === 'cancelled') {
            totalCancelledItemAmount += (Number(cost) + Number(core)) * Number(count);
          }
          const item = {
            title,
            image,
            partId,
            orderItemId,
            exportedDate,
            isExportedToAP,
            price,
            cost,
            core,
            actualCost: cost,
            actualCore: core,
            quantity: count,
            total: cost * count,
            description,
            partNumber,
            isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
            deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
            itemStatus: itemStatus ?? '',
            displayItemStatus: itemStatus ? { label: orderStatusLabel[itemStatus] } : { label: 'Delivered' },
            isTaxable: isTaxable,
          };

          if (!itemStatus && deliveryDate) {
            item.itemStatus = 'received';
          }

          return item;
        });

        orderDetail.totalExportedItems = totalExportedItems;
        orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;
        orderDetail.totalCoreCost = totalCoreCost;
        orderDetails.push(orderDetail);
      });
    } else if (type === 'tireConnect') {
      if (orderingType === 'purchaseOrder') {
        orders.forEach(({ type, supplier, items, fees = [], discounts = [], poNumber, warehouse }) => {
          const orderDetail: any = {};
          let totalExportedItems = 0;
          let totalCancelledItemAmount = 0;
          let totalCoreCost = 0;
          orderDetail.orderNo = poNumber;
          orderDetail.supplier = `${supplier?.name} - ${warehouse?.id}`;

          orderDetail.items = items.map(
            ({ orderItemId, exportedDate, isExportedToAP, title, price, cost, core, quantity, deliveryDate, itemStatus, orderStatus, description, inventoryItem, subTotal, partOrderId, _id, isTaxable }) => {
              totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
              if (itemStatus && itemStatus === 'cancelled') {
                totalCancelledItemAmount += (Number(cost) + Number(core)) * Number(quantity);
              }

              if (core) {
                totalCoreCost += Number(core) * Number(quantity);
              }
              const item = {
                orderItemId,
                partId: partOrderId,
                title: title,
                image: '',
                price: price,
                quantity,
                cost: cost,
                core: core,
                actualCost: cost,
                actualCore: core,
                total: 0,
                exportedDate,
                isExportedToAP,
                description: description ?? title,
                partNumber: inventoryItem?.code,
                defaultItemStatus: itemStatus || orderStatus,
                isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus || orderStatus), // its should brfore assign of default item status
                deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
                itemStatus: (itemStatus || orderStatus) ?? 'received',
                displayItemStatus: itemStatus || orderStatus ? { label: orderStatusLabel[itemStatus || orderStatus] } : { label: 'Delivered' },
                inventoryItem: inventoryItem,
                subTotal: subTotal,
                type: inventoryItem?.type || 'tire',
                _id,
                isTaxable: isTaxable,
              };

              //console.log('itemStatus', itemStatus);

              item.total = cost * item.quantity;
              return item;
            },
          );
          orderDetail.fees = fees;
          orderDetail.discounts = discounts;
          orderDetail.totalExportedItems = totalExportedItems;
          orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;
          orderDetail.totalCoreCost = totalCoreCost;
          orderDetails.push(orderDetail);
        });
      } else {
        orders.forEach(({ orderNo, warehouse, supplier, items }) => {
          const orderDetail: any = {};
          let totalExportedItems = 0;
          let totalCancelledItemAmount = 0;
          let totalCoreCost = 0;
          orderDetail.orderNo = orderNo;
          orderDetail.supplier = `${supplier?.name} - ${warehouse?.name}`;

          orderDetail.items = items.map(({ exportedDate, core, isExportedToAP, partId, orderItemId, title, image, cost, price, count, itemStatus, description, partNumber, deliveryDate, isTaxable }) => {
            if (itemStatus && itemStatus === 'cancelled') {
              totalCancelledItemAmount += (Number(cost) + Number(core)) * Number(count);
            }
            totalCoreCost += Number(core);
            totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
            const item = {
              title,
              image,
              partId,
              orderItemId,
              price,
              description: description ?? title,
              partNumber,
              exportedDate,
              isExportedToAP,
              cost,
              core,
              actualCost: cost,
              actualCore: core,
              quantity: count,
              total: cost * count,
              isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
              deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
              itemStatus: itemStatus ?? '',
              displayItemStatus: itemStatus ? { label: orderStatusLabel[itemStatus] } : { label: 'Delivered' },
              isTaxable: isTaxable,
            };

            if (!itemStatus && deliveryDate) {
              item.itemStatus = 'received';
            }

            return item;
          });

          orderDetail.totalCoreCost = totalCoreCost;
          orderDetail.totalExportedItems = totalExportedItems;
          orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;

          orderDetails.push(orderDetail);
        });
      }
    } else if (type === 'repairLink') {
      orders.forEach(({ orderNo, warehouse, supplier, items, fees, discounts }) => {
        const orderDetail: any = {};
        let totalExportedItems = 0;
        let totalCoreCost = 0;
        let totalCancelledItemAmount = 0;
        orderDetail.orderNo = orderNo;
        orderDetail.supplier = `${supplier?.name} - ${warehouse?.name}`;
        orderDetail.items = items.map(({ exportedDate, isExportedToAP, partId, orderItemId, title, image, price, cost, core, count, description, partNumber, deliveryDate, itemStatus, isTaxable }) => {
          if (itemStatus && itemStatus === 'cancelled') {
            totalCancelledItemAmount += (Number(cost) + Number(core)) * Number(count);
          }

          totalCoreCost += Number(core);

          totalExportedItems = isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
          const item = {
            title,
            exportedDate,
            isExportedToAP,
            image,
            partId,
            orderItemId,
            description,
            partNumber,
            price,
            cost,
            core,
            actualCost: cost,
            actualCore: core,
            quantity: count,
            total: (Number(cost) + Number(core)) * count,
            isAlreadyDelivered: ['received', 'cancelled'].includes(itemStatus), // its should brfore assign of default item status
            deliveredDate: deliveryDate ? new Date(deliveryDate) : new Date(),
            itemStatus: itemStatus ?? '',
            displayItemStatus: itemStatus ? { label: orderStatusLabel[itemStatus] } : { label: 'Delivered' },
            isTaxable: isTaxable,
          };

          if (!itemStatus && deliveryDate) {
            item.itemStatus = 'received';
          }

          return item;
        });
        orderDetail.fees = fees;
        orderDetail.discounts = discounts;
        orderDetail.totalExportedItems = totalExportedItems;
        orderDetail.totalCoreCost = totalCoreCost;
        orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;

        orderDetails.push(orderDetail);
      });
    } else if (type === 'speedDial') {
      let totalCancelledItemAmount = 0;
      let totalExportedItems = 0;
      let totalCoreCost = 0;
      const orderDetail: any = {};
      orderDetail.items = orders?.orderLines?.map((item) => {
        if (item?.itemStatus && item?.itemStatus === 'cancelled') {
          totalCancelledItemAmount += (Number(item.price) + Number(item.corePrice)) * Number(item.quantity);
        }

        totalCoreCost += Number(item.corePrice);

        totalExportedItems = item.isExportedToAP ? totalExportedItems + 1 : totalExportedItems;
        const part: any = {
          partName: item.productDesc,
          title: item.productDesc,
          partNumber: item.productID,
          partId: item.productID,
          qtyOrdered: item.quantity,
          orderItemId: [sessionId, item.productID, item.brandID].join('-'),
          imageUrl: '',
          deliveryStatus: item?.deliveryStatus,
          isExportedToAP: item?.isExportedToAP,
          description: item.productDesc,
          list: Number(item.listPrice),
          price: Number(item.listPrice),
          cost: Number(item.price),
          total: Number(item.price) * Number(item.quantity),
          core: Number(item.corePrice),
          quantity: Number(item.quantity),
          costWithCore: Number(item.price) + Number(item.corePrice),
          actualCost: Number(item.actualCost),
          actualCore: Number(item.actualCore),
          exportedDate: item.exportedDate,
          isAlreadyDelivered: item.isAlreadyDelivered,
          displayItemStatus: item.displayItemStatus,
          itemSource: 'SpeedDial',
          itemStatus: item.itemStatus || '',
          deliveryDate: item?.deliveryDate,
          cancelledDate: item?.cancelledDate,
          qtyReturned: item.qtyReturned ? item.qtyReturned : 0,
          // cartGroupId: cartGroupId || '',
          invoiceDate: item.invoiceDate,
          dueDate: item.dueDate,
          supplierInvoicedNo: item.supplierInvoicedNo,
          isTaxable: item.isTaxable,
        };
        return part;
      });
      orderDetail.totalExportedItems = totalExportedItems;
      orderDetail.totalCancelledItemAmount = totalCancelledItemAmount;
      orderDetail.totalCoreCost = totalCoreCost;
      orderDetails.push(orderDetail);
    }
    return orderDetails;
  }

  isVideoFile(fileExtension: string) {
    return new RegExp('mp4|3gp|m4v|mov|flv|wmv|avi', 'i').test(fileExtension);
  }

  isImageFile(fileExtension: string) {
    return new RegExp('jpg|jpeg|png|svg|gif', 'i').test(fileExtension);
  }

  setPaymentHistory(history: any, timezone: string = '') {
    let paymentHistory = [];
    if (history && history.length >= 0) {
      paymentHistory = [...history];
      paymentHistory.forEach((payment) => {
        if (!!payment.paymentDate) {
          payment.createdAt = this.dateService.formatDate(new Date(payment.paymentDate), datetimeFormat.listDT);
        } else {
          payment.createdAt = this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(payment.createdAt).getTime(), timezone), datetimeFormat.listDT);
        }

        if (payment.paymentType === 'cash') {
          payment.paymentType = 'Cash';
        }
        // this.allRefunds = this.allRefunds + payment.refundToDate;
        if (payment.paymentType === 'card' && !payment.isCardSwipe) {
          payment.paymentType = payment.isOnline ? 'Card-online' : 'Card-manual';
          const paymentTypePDF = payment.paymentType;
          if (payment.card4Digits) {
            payment.paymentType = `${payment.paymentType} (* ${payment.card4Digits})`;
            payment.paymentTypePDF = `${paymentTypePDF} xxxx-xxxx-${payment.card4Digits}`;
          } else {
            payment.paymentTypePDF = payment.paymentType;
          }
        } else if (payment.isCardSwipe) {
          payment.paymentType = 'Card swap';
          payment.paymentTypePDF = 'Card swap';
        } else {
          payment.paymentTypePDF = payment.paymentType;
        }

        payment.refunds.map((refund) => {
          refund.refundDate = this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(refund.refundDate).getTime(), timezone), datetimeFormat.listDT);
        });
        // sort refunds
        payment.refunds = orderBy(payment.refunds, ['refundDate'], 'desc');
      });
    }
    return paymentHistory;
  }

  logout(options: any = null) {
    this.cookieService.removeAll();
    localStorage.setItem('currentShop', '');
    localStorage.removeItem('retain-date');
    if (this.dataService.isGainSightPXEnabled) (<any>window).aptrinsic('reset');

    if (environment.site === 'prod') {
      const navigationExtras: NavigationExtras = {
        state: options,
      };
      this.router.navigate(['login'], navigationExtras);
      window.location.reload();
    } else {
      const query: any = {};

      if (options) {
        if (options.errorMessage) {
          query.errorMessage = options.errorMessage;
        }
      }

      const url = this.constructQueryParams(`${environment.app2_base_url}#/login`, query);
      window.location.replace(url);
      window.location.reload();
    }
  }

  getDepostDialogPaidContant(code, companyNumber, status, currentLang?): any {
    let value;
    if (status === 'paid') {
      if (currentLang && currentLang.toLowerCase() === 'fr') {
        value = {
          title: 'Dépôt déjà payé!',
          msg: `<div>Le dépôt contre l'estimation ${code}
          a déjà été payé. Veuillez nous contacter au
          <a href="tel:${companyNumber}">
            ${this.formatPhoneNumber(companyNumber)}
          </a>
          en cas de questions.</div>`,
        };
      } else {
        value = {
          title: 'You have already paid the Deposit!',
          msg: `<div>The deposit against Estimate ${code}
        has already been paid. Please contact us at
        <a href="tel:${companyNumber}">
          ${this.formatPhoneNumber(companyNumber)}
        </a>
        in case of any queries.</div>`,
        };
      }
    } else if (status === 'invoiced') {
      if (currentLang && currentLang.toLowerCase() === 'fr') {
        value = {
          title: 'Demande de dépôt annulée!',
          msg: `<div>Cette RO a maintenant été facturée. Vous pouvez payer le montant de la facture
          en cliquant sur le bouton “Payer maintenant” sur la demande de dépôt.
          Veuillez nous contacter au
          <a href="tel:${companyNumber}">
            ${this.formatPhoneNumber(companyNumber)}
          </a>
          en cas de questions.</div>`,
        };
      } else {
        value = {
          title: 'Deposit request canceled!',
          msg: `<div>This RO has now been invoiced. You can pay the Invoice amount
        by clicking on the “Pay now” button on the deposit request.
        Please contact us at
        <a href="tel:${companyNumber}">
          ${this.formatPhoneNumber(companyNumber)}
        </a>
        in case of any queries</div>`,
        };
      }
    } else if (status === 'canceled') {
      if (currentLang && currentLang.toLowerCase() === 'fr') {
        value = {
          title: 'Demande de dépôt annulée!',
          msg: `<div>Le magasin a annulé cette demande de dépôt. Veuillez nous contacter au
          <a href="tel:${companyNumber}">
            ${this.formatPhoneNumber(companyNumber)}
          </a>
          pour plus de détails.</div>`,
        };
      } else {
        value = {
          title: 'Deposit request canceled!',
          msg: `<div>
          The shop has canceled this deposit request. Please contact us at
          <a href="tel:${companyNumber}">
            ${this.formatPhoneNumber(companyNumber)}
          </a> for more details.
        </div>`,
        };
      }
    }
    return value;
  }

  parseQueryParams() {
    try {
      const query: any = {};
      const url = window.location.hash;
      if (url.indexOf('?') >= 0) {
        url
          .substring(url.indexOf('?') + 1, url.length)
          .split('&')
          .forEach((str: string) => {
            const [key, value] = str.split('=');
            query[key] = value;
          });
      }

      return query;
    } catch (err) {
      return null;
    }
  }

  setCompanyRefInCookies(companyId: string) {
    try {
      this.cookieService.put('cid', companyId, { domain: '.myautoleap.com' });
      this.cookieService.put('cid1', companyId);
    } catch (err) {
      console.log(err);
    }
  }

  getInspectionStatus(category) {
    const value: any = {};
    if (category.colorIndex === 0) {
      value.name = 'Green';
      value.class = 'green';
    } else if (category.colorIndex === 1) {
      value.name = 'Yellow';
      value.class = 'yellow';
    } else if (category.colorIndex === 2) {
      value.name = 'Red';
      value.class = 'red';
    } else {
      value.name = 'noStatus';
      value.class = '';
    }
    return value;
  }

  createCreditsIssuedTooltip(payment: any) {
    const history = payment.history;

    if (history.length) {
      let tooltip = '';
      history.forEach(({ action, fullName, amount, createdAt }) => {
        tooltip = `
          <div class="tooltip-2">
              <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex payment-history-wrapper">
                <div class="payment-history font-800 font-gray-35 d-flex mb-0">
                  <i class="fal fa-plus-square align-self-start pt-1"></i>
                  <span class="payment-history-title pl-3px">
                    <b class="font-600">${action === 'create' ? 'Created' : 'Modified'} by ${fullName}</b>
                  </span>
                </div>
                <div class="payment-amount">
                  <div class="payment-mode">
                    <i class="fal fa-paper-plane align-self-start pt-1"></i>
                    <span class="text-capitalize">
                    ${this.dateService.formatDate(payment.date ? new Date(payment.date) : this.dateService.getCurrentDateTimeTz(new Date(createdAt).getTime()), datetimeFormat.mmmdyyhhmmaa)}
                    </span>
                    </div>
                  <div class="paid-amount">
                    <span>$${this.formatAmount(amount.toFixed(2))}</span>
                  </div>
                </div>
              </div>
            </div>
          ${tooltip}`;
      });

      tooltip = `

      <div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-bold font-secondary-800 divider-bottom">
            <i class="fal fa-usd-circle"></i>Credits issued
      </div>
      ${tooltip}
      </div>`;
      return tooltip;
    }

    return '';
  }

  formatItem(orderedItem: any, itemSource: string) {
    const item: any = {
      isTaxable: false,
      displayDescOnOrder: false,
      displayHoursOnOrder: false,
      discount: { value: 0, type: 'percentage', name: 'Percent' },
    };

    if (itemSource === 'Nexpart') {
      orderedItem.quantity = Number(orderedItem.OrderQty);
      orderedItem.supplier = { name: orderedItem.supplier.name };
      orderedItem.partName = orderedItem.SellerPartDesc;
      orderedItem.partNumber = orderedItem.PartNumber;
      orderedItem.description = orderedItem.CatalogPartDesc;
      orderedItem.orderItemId = orderedItem.orderItemId;
      orderedItem.partId = orderedItem.PartNumber;
      orderedItem.lineCardId = orderedItem.LineCode;
      orderedItem.qtyReturned = orderedItem.qtyReturned;
      orderedItem.itemSource = 'Nexpart';
      orderedItem.price = {
        list: Number(orderedItem.List),
        price: Number(orderedItem.List),
        cost: Number(orderedItem.Mdse),
        core: Number(orderedItem.Core),
        costWithCore: Number(orderedItem.Mdse) + Number(orderedItem.Core),
      };
      orderedItem.amount = orderedItem.quantity * orderedItem.price.cost;
    } else if (itemSource === 'SpeedDial') {
      orderedItem.price = {
        list: Number(orderedItem.listPrice),
        price: Number(orderedItem.listPrice),
        cost: Number(orderedItem.price),
        core: Number(orderedItem.core),
        costWithCore: Number(orderedItem.price) + Number(orderedItem.core || 0),
      };
      orderedItem.amount = orderedItem.quantity * orderedItem.price.cost;
    } else if (itemSource === 'RepairLink') {
      orderedItem.price = {
        list: Number(orderedItem.price),
        price: Number(orderedItem.price),
        cost: Number(orderedItem.cost),
        core: Number(orderedItem.core),
        costWithCore: Number(orderedItem.cost || 0) + Number(orderedItem.core || 0),
      };
      orderedItem.amount = orderedItem.quantity * orderedItem.price.cost;
    }

    item.type = orderedItem.type || 'part';
    item.code = orderedItem.partNumber || orderedItem.code;
    item.title = orderedItem.partName || orderedItem.title;
    item.description = orderedItem.description || orderedItem.partNumber || '';
    item.itemSource = orderedItem.itemSource;
    item.poNumber = orderedItem.poNumber;
    item.supplier = orderedItem.supplier;
    item.count = orderedItem.quantity;
    item.price = orderedItem.price.list || orderedItem.price;
    item.cost = orderedItem.price.cost || orderedItem.cost;
    item.actualCost = orderedItem.price.cost || orderedItem.cost;
    item.coreCost = orderedItem.price.core;
    item.partOrderId = orderedItem.id;
    item.orderItemId = orderedItem.orderItemId;
    item.lineCartId = orderedItem.lineCardId;
    item.itemSourceId = orderedItem.partId;
    item.subTotal = item.price * item.count;
    item.numericItemId = orderedItem.numericItemId;

    if (
      (orderedItem.taxonomy && new RegExp('^tire*', 'i').test(orderedItem.taxonomy.partTypeName) && !toLower(orderedItem.taxonomy.partTypeName).includes('sensor')) ||
      (orderedItem.type && orderedItem.type.toLowerCase() === 'tire')
    ) {
      item.type = 'tire';
      item.seasonality = orderedItem.seasonality;
      item.size = orderedItem.tireSize || orderedItem.size;
      const [brand, ...model] = item.title.split(' ');
      // item.category = { name: brand };  we dont want to show category as brand in any case ALP-16612
      item.model = model.join(' ');

      if (orderedItem.attributes && orderedItem.attributes.length) {
        item.description = orderedItem.attributes.map(({ label, value }) => `${label}: ${value}`).join(', ');

        const season = find(orderedItem.attributes, { label: 'Category' });

        if (season) {
          if (new RegExp('all season', 'i').test(season.value)) item.seasonality = 'all';
          else if (new RegExp('winter', 'i').test(season.value)) item.seasonality = 'winter';
          else if (new RegExp('summer', 'i').test(season.value)) item.seasonality = 'summer';
        }
      }
    }

    return item;
  }

  getPartsOrderingItem(order: any, itemSource: string, orderItemId: string) {
    const { partsTechQoutes, nexpartQoutes, tireHubQoutes, manualQoutes, repairLinkQoutes, speedDialQoutes } = this.formatOrderingQoutes(order);

    if (itemSource === 'ManualOrder') {
      for (let i = 0; i < order.services.length; i++) {
        const service = order.services[i];

        if (service.items.length) {
          for (let j = 0; j < service.items.length; j++) {
            const item = service.items[j];

            if (item._id === orderItemId) {
              return item;
            }
          }
        }
      }
    } else if (itemSource === 'PartsTech') {
      for (let i = 0; i < partsTechQoutes.length; i++) {
        const partsTechQoute = partsTechQoutes[i];

        if (partsTechQoute?.partsTechQoute?.orders?.length) {
          for (let j = 0; j < partsTechQoute.partsTechQoute.orders.length; j++) {
            const order = partsTechQoute.partsTechQoute.orders[j];

            if (order?.parts?.length) {
              for (let k = 0; k < order.parts.length; k++) {
                const partsTechItem = order.parts[k];

                if (partsTechItem.orderItemId === orderItemId) {
                  const item = {
                    cartId: partsTechQoute.sessionId,
                    orderItemId: partsTechItem.orderItemId,
                    type: 'part',
                    title: partsTechItem.partName,
                    description: partsTechItem.description,
                    code: partsTechItem.partNumber,
                    isTaxable: partsTechItem.isTaxable,
                    price: Number(partsTechItem.price.list),
                    stocked: partsTechItem.stocked,
                    supplier: { name: order.supplier && order.supplier.name },
                    subTotal: 0,
                    supplierInvoicedNo: '',
                    cost: Number(partsTechItem.price.cost),
                    quantity: Number(partsTechItem.quantity),
                    usedQty: 0,
                    unUsedQty: 0,
                    qtyReturned: partsTechItem.qtyReturned,
                    totalCost: 0,
                    itemSourceId: partsTechItem.partId,
                    qtyAddedToInventory: '' as string | number,
                    itemReturnQty: '' as string | number,
                    returnDate: new Date(),
                    itemSource: 'PartsTech',
                    isConsumed: false,
                    size: '',
                    category: null,
                    model: '',
                    seasonality: '',
                    poNumber: order.poNumber,
                    taxonomy: partsTechItem.taxonomy,
                    attributes: partsTechItem.attributes,
                  };

                  if (partsTechItem.taxonomy && new RegExp('^tire*', 'i').test(partsTechItem.taxonomy.partTypeName) && !toLower(partsTechItem.taxonomy.partTypeName).includes('sensor')) {
                    item.type = 'tire';
                    item.size = partsTechItem.tireSize;
                    const [brand, ...model] = item.title.split(' ');
                    // item.category = { name: brand };  we dont want to show category as brand in any case ALP-16612
                    item.model = model.join(' ');

                    if (partsTechItem.attributes && partsTechItem.attributes.length) {
                      item.description = partsTechItem.attributes.map(({ label, value }) => `${label}: ${value}`).join(', ');

                      const season = find(partsTechItem.attributes, { label: 'Category' });

                      if (season) {
                        if (new RegExp('all season', 'i').test(season.value)) item.seasonality = 'all';
                        else if (new RegExp('winter', 'i').test(season.value)) item.seasonality = 'winter';
                        else if (new RegExp('summer', 'i').test(season.value)) item.seasonality = 'summer';
                      }
                    }
                  }

                  item.subTotal = item.cost * item.quantity;
                  item.totalCost = item.subTotal;
                  item.isConsumed = item.quantity === item.qtyReturned;
                  
                  const formattedItem = this.formatItem(item, 'PartsTech');
                  formattedItem.hasCore = !!Number(partsTechItem?.price?.core);
                  formattedItem.coreCost = partsTechItem?.price?.core;
                  return formattedItem;

                  //return this.formatItem(item, 'PartsTech');
                }
              }
            }
          }
        }
      }
    } else if (itemSource === 'Nexpart') {
      // Nexpart Qoutes
      for (let i = 0; i < nexpartQoutes?.length; i++) {
        const nexpartQoute = nexpartQoutes[i];
        if (nexpartQoute.isOrderDelivered) {
          if (nexpartQoute.quote.Cart && nexpartQoute.quote.Cart.BranchCart && nexpartQoute.quote.Cart.BranchCart.length) {
            for (let j = 0; j < nexpartQoute.quote.Cart.BranchCart.length; j++) {
              const order = nexpartQoute.quote.Cart.BranchCart[j];
              if (order.Parts && order.Parts.Part && order.Parts.Part?.length) {
                for (let k = 0; k < order.Parts.Part?.length; k++) {
                  const nexpartItem = order.Parts.Part[k];
                  nexpartItem.itemSourceId = [nexpartQoute.sessionId, order.SellerId, order.BranchId, nexpartItem.LineCode, nexpartItem.PartNumber].join('-');
                  if (nexpartItem.itemSourceId === orderItemId) {
                    const item = {
                      ...nexpartItem,
                      cartId: nexpartQoute.sessionId,
                      orderItemId: nexpartItem.itemSourceId,
                      type: 'part',
                      title: nexpartItem.SellerPartDesc,
                      description: nexpartItem.CatalogPartDesc,
                      code: nexpartItem.PartNumber,
                      isTaxable: nexpartItem.isTaxable,
                      price: Number(nexpartItem.List),
                      stocked: nexpartItem.stocked,
                      supplier: { name: order.Name },
                      subTotal: 0,
                      supplierInvoicedNo: '',
                      cost: Number(nexpartItem.Mdse),
                      quantity: Number(nexpartItem.OrderQty),
                      usedQty: 0,
                      unUsedQty: 0,
                      qtyReturned: nexpartItem.qtyReturned,
                      totalCost: nexpartItem.cost * nexpartItem.quantity,
                      qtyAddedToInventory: '' as string | number,
                      itemReturnQty: '' as string | number,
                      returnDate: new Date(),
                      itemSource: 'Nexpart',
                      isConsumed: false,
                      poNumber: nexpartQoute.poNumber,
                    };
                    item.subTotal = item.cost * item.quantity;
                    item.totalCost = item.subTotal;
                    item.isConsumed = item.quantity === item.qtyReturned;

                    const formattedItem = this.formatItem(item, 'Nexpart');
                    formattedItem.hasCore = !!Number(nexpartItem?.Core);
                    return formattedItem;
                  }
                }
              }
            }
          }
        }
      }
    } else if (itemSource === 'TireHub') {
      for (let i = 0; i < tireHubQoutes.length; i++) {
        const tirehubQoute = tireHubQoutes[i];

        if (tirehubQoute.isOrderDelivered && tirehubQoute?.quote?.orders?.length) {
          for (let j = 0; j < tirehubQoute.quote.orders.length; j++) {
            const order = tirehubQoute.quote.orders[j];

            if (order?.items?.length) {
              for (let k = 0; k < order.items.length; k++) {
                const tireHubItem = order.items[k];

                if (tireHubItem.orderItemId === orderItemId) {
                  const item = {
                    cartId: tirehubQoute.sessionId,
                    orderItemId: tireHubItem.orderItemId,
                    type: 'tire',
                    title: tireHubItem.title,
                    description: tireHubItem.description,
                    code: tireHubItem.code,
                    isTaxable: tireHubItem.isTaxable,
                    price: Number(tireHubItem.price),
                    stocked: tireHubItem.stocked,
                    supplier: { name: tireHubItem?.warehouse?.name },
                    subTotal: 0,
                    supplierInvoicedNo: '',
                    cost: Number(tireHubItem.unitPrice),
                    quantity: Number(tireHubItem.count),
                    usedQty: 0,
                    unUsedQty: 0,
                    qtyReturned: tireHubItem.qtyReturned,
                    totalCost: tireHubItem.unitPrice * tireHubItem.count,
                    qtyAddedToInventory: tireHubItem.qtyAddedToInventory,
                    itemReturnQty: '' as string | number,
                    returnDate: new Date(),
                    itemSource: 'TireHub',
                    isConsumed: false,
                    poNumber: tirehubQoute.poNumber,
                    seasonality: tireHubItem.seasonality,
                    size: tireHubItem.size,
                    numericItemId: tireHubItem.numericItemId,
                  };

                  item.subTotal = item.cost * item.quantity;
                  item.totalCost = item.subTotal;
                  item.isConsumed = item.quantity === item.qtyReturned;

                  return this.formatItem(item, 'TireHub');
                }
              }
            }
          }
        }
      }
    } else if (itemSource === 'RepairLink') {
      for (let i = 0; i < repairLinkQoutes.length; i++) {
        const repairLinkQoute = repairLinkQoutes[i];

        if (repairLinkQoute.isOrderDelivered && repairLinkQoute?.quote?.orders?.length) {
          for (let j = 0; j < repairLinkQoute.quote.orders.length; j++) {
            const order = repairLinkQoute.quote.orders[j];

            if (order?.items?.length) {
              for (let k = 0; k < order.items.length; k++) {
                const repairLinkItem = order.items[k];

                if (repairLinkItem.orderItemId === orderItemId) {
                  const item = {
                    cartId: repairLinkQoute.sessionId,
                    orderItemId: repairLinkItem.orderItemId,
                    type: 'part',
                    title: repairLinkItem.title,
                    description: repairLinkItem.description,
                    code: repairLinkItem.partNumber,
                    isTaxable: repairLinkItem.isTaxable,
                    price: Number(repairLinkItem.price),
                    stocked: repairLinkItem.stocked,
                    supplier: { name: order?.supplier?.name },
                    subTotal: 0,
                    supplierInvoicedNo: '',
                    cost: Number(repairLinkItem.cost),
                    quantity: Number(repairLinkItem.count),
                    usedQty: 0,
                    unUsedQty: 0,
                    qtyReturned: repairLinkItem.qtyReturned,
                    totalCost: repairLinkItem.cost * repairLinkItem.count,
                    qtyAddedToInventory: repairLinkItem.qtyAddedToInventory,
                    itemReturnQty: '' as string | number,
                    returnDate: new Date(),
                    itemSource: 'RepairLink',
                    isConsumed: false,
                    poNumber: repairLinkQoute.poNumber,
                    seasonality: repairLinkItem.seasonality,
                  };

                  item.subTotal = item.cost * item.quantity;
                  item.totalCost = item.subTotal;
                  item.isConsumed = item.quantity === item.qtyReturned;

                  return this.formatItem(item, 'RepairLink');
                }
              }
            }
          }
        }
      }
    } else if (itemSource === 'SpeedDial') {
      for (let i = 0; i < speedDialQoutes.length; i++) {
        const speedDialQoute = speedDialQoutes[i];

        if (speedDialQoute.isOrderDelivered && speedDialQoute?.quote && speedDialQoute?.quote?.order?.orderLines?.length) {
          for (let j = 0; j < speedDialQoute.quote.order?.orderLines.length; j++) {
            const lineItem = speedDialQoute.quote.order.orderLines[j];

            const getOrderItemIdFromQoute = [speedDialQoute.internalReferenceId, lineItem.productID, lineItem.brandID].join('-');
            if (getOrderItemIdFromQoute === orderItemId) {
              const item = {
                cartId: speedDialQoute.sessionId,
                orderItemId: getOrderItemIdFromQoute,
                type: 'part',
                title: lineItem.productDesc,
                description: lineItem.productDesc,
                code: lineItem.productID,
                isTaxable: lineItem.isTaxable,
                stocked: lineItem.stocked,
                supplier: { name: speedDialQoute?.supplier?.name },
                subTotal: 0,
                supplierInvoicedNo: '',

                price: Number(lineItem.listPrice),
                cost: Number(lineItem.price),
                quantity: Number(lineItem.quantity),
                list: Number(lineItem.listPrice),
                core: Number(lineItem.core),
                costWithCore: Number(lineItem.price) + Number(lineItem.core || 0),
                totalPrice: Number(lineItem.quantity) * Number(lineItem.listPrice),
                usedQty: 0,
                unUsedQty: 0,
                qtyReturned: lineItem.qtyReturned,
                totalCost: Number(lineItem.price) * Number(lineItem.quantity),
                qtyAddedToInventory: lineItem.qtyAddedToInventory,
                itemReturnQty: '' as string | number,
                // returnDate: new Date(),
                itemSource: 'SpeedDial',
                isConsumed: false,
                poNumber: speedDialQoute.poNumber,
                seasonality: lineItem.seasonality,
                count: Number(lineItem.quantity),
              };

              // item.subTotal = item.cost * item.quantity;
              // item.totalCost = item.subTotal;
              // item.isConsumed = Number(lineItemitem.quantity === item.qtyReturned;

              return item;
            }
          }
        }
      }
    }
  }

  getPaymentTerms() {
    let paymentTerms = [];

    if (this.dataService.accountPayablePayment && this.dataService.accPayableTerms?.length) {
      const isQuickbooksConnected = this.getCompany().qboId;

      paymentTerms = this.dataService.accPayableTerms.filter(({ qboId }) => {
        if (isQuickbooksConnected) {
          return !!qboId;
        } else {
          return !qboId;
        }
      });
    }
    return paymentTerms;
  }

  formatTireInfoForCustomerScreens(item: any, lowerTreadDepthThreshold: any) {
    try {
      item.tiresInfo = item.tiresInfo.slice(0, 6);
      item.fl = { label: 'FL: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      item.fr = { label: 'FR: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      item.rl = { label: 'RL: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      item.rr = { label: 'RR: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      item.s1 = { label: 'S1: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      item.s2 = { label: 'S2: ', isExist: false, treadDepth: '', isReplacementRecommended: false };
      const tiresWithValidData = item.tiresInfo.filter(({ tirePosition, treadDepth }) => tirePosition && this.getTreadDepth(treadDepth).isValid).length;

      if (tiresWithValidData === item.tiresInfo?.length) {
        item.tiresInfo.forEach((tireInfo: any, index: number) => {
          const tirePosition: any = find(tirePositionOptions, { value: tireInfo?.tirePosition || '' });
          const lowerTreadDepth = this.calculateService.roundOff(lowerTreadDepthThreshold);

          if (tirePosition) {
            let treadDepthText: any;
            const { isValid, treadDepth } = this.getTreadDepth(tireInfo?.treadDepth);

            if (isValid && treadDepth >= 1 && treadDepth <= 32) {
              treadDepthText = `${this.calculateService.roundOff(treadDepth)}/32"`;
            } else {
              treadDepthText = tireInfo?.treadDepth;
            }

            if (!treadDepthText && item[toLower(tirePosition.value)]) {
              item[toLower(tirePosition.value)].label = item[toLower(tirePosition.value)].label.replace(': ', '');
            }

            item[toLower(tirePosition.value)] = {
              label: item[toLower(tirePosition.value)]?.label,
              isExist: true,
              treadDepth: treadDepthText,
              isReplacementRecommended: tireInfo?.recommendReplacement || (treadDepth && treadDepth <= lowerTreadDepth),
            };
          }
        });
      } else {
        item.tiresInfo = [];
      }
    } catch (err) {
      console.log(err);
      item.tiresInfo = [];
    }
    return item;
  }

  getTreadDepth(treadDepth: string = '', isFormatNeed: boolean = false) {
    const data: {
      treadDepth: any | number;
      isValid: boolean;
    } = { treadDepth: '', isValid: true };

    if (!treadDepth) return data;

    try {
      data.treadDepth = treadDepth;

      if (new RegExp(/^[0-9]+$/).test(treadDepth)) {
        data.treadDepth = Number(treadDepth);
      } else if (new RegExp(/^[0-9]+\/32$/).test(treadDepth) || new RegExp(/^[0-9]+\/32"$/).test(treadDepth)) {
        data.treadDepth = Number(treadDepth.split('/')[0]);
      } else {
        data.isValid = false;
      }

      if (data.isValid && (Number(data.treadDepth) < 1 || Number(data.treadDepth) > 32)) {
        data.isValid = false;
      }

      if (data.isValid && isFormatNeed) {
        data.treadDepth = `${data.treadDepth}/32"`;
      }
    } catch (err) {
      console.log(err);
    } finally {
      return data;
    }
  }

  generateTooltip(data?, type?) {
    let tooltip = ``;

    if (type === 'manulaOrder') {
      data.forEach((service) => {
        service.items.forEach(({ title, exportedDate }) => {
          if (exportedDate)
            tooltip += `<div class="flex-start-center tax-tooltip-row-tags">
                          <span class="span-tax">${title || ''} </span>
                          <span class="span-date pr-2">${exportedDate ? this.dateService.formatDate(new Date(exportedDate), datetimeFormat.mmmdyyyy) : '-'}</span>
                </div>`;
        });
      });
      return `<div class="flex-column">${tooltip}</div>`;
    } else {
      try {
        if (data) {
          data.forEach(({ title, exportedDate }) => {
            if (exportedDate)
              tooltip += `<div class="flex-start-center tax-tooltip-row-tags">
                          <span class="span-tax">${title || ''} </span>
                          <span class="span-date pr-2">${exportedDate ? this.dateService.formatDate(new Date(exportedDate), datetimeFormat.mmmdyyyy) : '-'}</span>
                </div>
                `;
          });
        }
        return `<div class="flex-column">${tooltip}</div>`;
      } catch (err) {
        console.error(err);
      }
    }
  }

  formatTextCapitalize(status) {
    let text = '';
    if (status && status.value) {
      text = status.value[0].toUpperCase() + status.value.slice(1);
    }
    return text;
  }

  generateTooltipTagList(data, source, type, orderStatus?) {
    let tooltip = '';

    // Manual order other than retuend and in case of not delivered
    if (type === 'quantity' && source === 'manualOrder' && orderStatus !== 'received') {
      tooltip = `${itemOrderStatusLabel[data?.orderStatus]}`;
      return `<div class="flex-column p-1">${tooltip}</div>`;
    } else if (['nexpart', 'partstech', 'tireHub', 'tireConnect', 'speedDial'].includes(source) && orderStatus !== 'received' && type !== 'returned') {
      tooltip = `${itemOrderStatusLabel[data?.orderStatus || orderStatus]}`;
      return `<div class="flex-column p-1">${tooltip == 'undefined' ? 'Returned: ' + data?.qtyReturned : tooltip}</div>`;
    }
    //  order quantity for delivered items
    if (type === 'quantity' && orderStatus === 'received') {
      tooltip = `<div class="flex-start-center tax-tooltip-row-tags" style="width: 290px;">
                  <span class="pr-1 tax-name header">Supplier name</span>
                  <span class="tax-name header">Quantity Delivered & Date</span>
                  </div>`;
      if (source === 'manualOrder') {
        data.forEach((item) => {
          if (item.orderStatus === 'received') {
            tooltip += `<div class="tax-tooltip-row-tags pr-2 pt-1 pl-1">
                          <span class="pr-1 tax-name">${item?.supplier?.name}</span>
                          <span class="span-tax"> ${item?.quantity ? 'QT: ' + item?.quantity : ''}: <br />${item?.orderedAt ? 'Mark delivered at this date ' + item?.orderedAt : '-'}</span>
                    </div>
                    `;
          }
        });
      } else {
        tooltip += `<div class="tax-tooltip-row-tags pr-2 pt-1 pl-1">
                          <span class="pr-1 tax-name">${data?.supplier}</span>
                          <span class="span-tax"> ${data?.quantity ? 'QT: ' + data?.quantity : ''} <br /> ${data.qtyAddedToInventory ? 'Added to Inventory: ' + data.qtyAddedToInventory + '<br /> <br />' : ''} ${data?.deliveryDate ? 'Mark delivered at this date ' + this.dateService.formatDate(new Date(data.deliveryDate), datetimeFormat.mmmdyyhhmmaa) : '-'

          }</span>
                  </div>`;
      }
    } else if (type === 'returned') {
      //  tooltip for return items
      tooltip = `<div class="flex-start-center tax-tooltip-row-tags" style="width: 290px;"">
    <span class="pr-1 tax-name header">Supplier&nbspname</span>
    <span class="tax-name header">Quantity&nbspReturn&nbsp&&nbspDate</span>
    </div>`;
      if (source === 'manualOrder') {
        data.forEach((item) => {
          if (item?.qtyReturned) {
            tooltip += `<div class="tax-tooltip-row-tags pr-2 pt-1 pl-1">
            <span class="pr-1 tax-name">${item?.supplier?.name}</span>
            <span class="span-tax"> ${item?.qtyReturned ? 'QT: ' + item?.qtyReturned : ''}: <br />${item?.itemReturnDate ? 'at ' + this.dateService.formatDate(new Date(item?.itemReturnDate), datetimeFormat.mmmdyyhhmmaa) : '-'
              }</span>
      </div>
      `;
          }
        });
      } else if (['nexpart', 'partstech', 'tireHub', 'tireConnect', 'speedDial'].includes(source)) {
        tooltip += `<div class="tax-tooltip-row-tags pr-2 pt-1 pl-1">
            <span class="pr-1 tax-name">${data?.supplier}</span>
            <span class="span-tax"> ${data?.qtyReturned ? 'Returned: ' + data?.qtyReturned : ''}: <br />${data?.itemReturnDate ? 'at ' + this.dateService.formatDate(new Date(data.itemReturnDate), datetimeFormat.mmmdyyhhmmaa) : '-'
          }</span>
    </div>`;
      }
    }

    return `<div class="flex-column p-1">${tooltip}</div>`;
  }

  shortNumber(number) {
    let roundNumber = this.calculationService.roundOff(number);
    if (isNaN(roundNumber)) return null; // will only work value is a roundNumber
    if (roundNumber === null) return null;
    if (roundNumber === 0) return 0;
    let abs = Math.abs(roundNumber);
    const rounder = Math.pow(10, 1);
    const isNegative = roundNumber < 0; // will also work for Negetive roundNumbers
    let key = '';

    const powers = [
      { key: 'q', value: Math.pow(10, 15) },
      { key: 't', value: Math.pow(10, 12) },
      { key: 'b', value: Math.pow(10, 9) },
      { key: 'm', value: Math.pow(10, 6) },
      { key: 'k', value: 1000 },
    ];

    for (let i = 0; i < powers.length; i++) {
      let reduced = abs / powers[i].value;
      reduced = Math.round(reduced * rounder) / rounder;
      if (reduced >= 1) {
        abs = reduced;
        key = powers[i].key;
        break;
      }
    }
    return (isNegative ? '-' : '') + abs + key;
  }

  textCapitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  setConfirmationStatus(status: boolean, id: string) {
    this.confirmationSubject.next({ status, id });
  }

  logPXGaingSightEvent(eventName, logInfo: any) {
    logInfo.tenantName = this.getCompany().companyName;
    //console.log(eventName, logInfo);
    (<any>window).aptrinsic('track', eventName, logInfo);
  }

  onCategorySelect(event: any, salesCodeId = '') {
    const data = {
      category: {} as any,
      salesCode: {} as any,
      categorySalesCode: {} as any,
      subCategorySalesCode: {} as any,
      isAutoAssign: false as boolean,
    };

    if (!event.level1 && event.category?.categoryId) {
      event.level1 = event.category;
      // event.level1._id = event._id;

      if (!event.level2 && event.subCategory?.subCategoryId) {
        event.level2 = event.subCategory;
        // event.level2._id = event._id;

        if (!event.level3 && event.subSubCategory?.subSubCategoryId) {
          event.level3 = event.subSubCategory;
          // event.level3._id = event._id;
        }
      }
    }

    if (event.level1) {
      data.category._id = event.level1._id;
      data.category.name = event.level1.categoryName;
      data.category.categoryId = event.level1.categoryId;
      data.category.categoryName = event.level1.categoryName;
      data.salesCode = event.level1.salesCode?.salesCodeId ? event.level1.salesCode : event.salesCode ? event.salesCode : null;
      data.categorySalesCode = event.level1.categorySalesCode?.salesCodeId ? event.level1.categorySalesCode : event.categorySalesCode ? event.categorySalesCode : null;
      data.subCategorySalesCode = event.level1.subCategorySalesCode?.salesCodeId ? event.level1.subCategorySalesCode : event.subCategorySalesCode ? event.subCategorySalesCode : null;

      if (event.level2) {
        data.category._id = event.level2._id;
        data.category.name += `/${event.level2.subCategoryName}`;
        data.category.subCategoryId = event.level2.subCategoryId;
        data.category.subCategoryName = event.level2.subCategoryName;
        data.salesCode = event.level2.salesCode?.salesCodeId ? event.level2.salesCode : event.salesCode ? event.salesCode : null;
        data.categorySalesCode = event.level2.categorySalesCode?.salesCodeId ? event.level2.categorySalesCode : event.categorySalesCode ? event.categorySalesCode : null;
        data.subCategorySalesCode = event.level2.subCategorySalesCode?.salesCodeId ? event.level2.subCategorySalesCode : event.subCategorySalesCode ? event.subCategorySalesCode : null;

        if (event.level3) {
          data.category._id = event.level3._id;
          data.category.name += `/${event.level3.subSubCategoryName}`;
          data.category.subSubCategoryId = event.level3.subSubCategoryId;
          data.category.subSubCategoryName = event.level3.subSubCategoryName;
          data.salesCode = event.level3.salesCode?.salesCodeId ? event.level3.salesCode : event.salesCode ? event.salesCode : null;
          data.categorySalesCode = event.level3.categorySalesCode?.salesCodeId ? event.level3.categorySalesCode : event.categorySalesCode ? event.categorySalesCode : null;
          data.subCategorySalesCode = event.level3.subCategorySalesCode?.salesCodeId ? event.level3.subCategorySalesCode : event.subCategorySalesCode ? event.subCategorySalesCode : null;
        }
      }
    }

    if (!data.category._id) {
      data.category._id = event._id;
    }

    if ((event.salesCode?.salesCodeId && event.salesCode?.salesCodeId !== salesCodeId) || (data.salesCode?.salesCodeId && data.salesCode?.salesCodeId !== salesCodeId)) {
      this.showToaster('success', 'Sales Code was updated automatically based on the new item classification');
      data.isAutoAssign = true;
    }

    data.salesCode = this.assignSalesCode(data);

    return data;
  }

  getItemTitle(item: any) {
    if (!item) return;
    return [item.category?.categoryName, item.category?.subCategoryName, item.category?.subSubCategoryName].filter((str) => str).join(' > ');
  }

  getUnassignedSalesCodeServiceItems(estimateId: string, services: any[] = [], serviceStatusMap = null): any {
    let unassignedServiceItems: any = [];
    let skippedItems: any = {};

    if (services.length) {
      let serviceArray: any = [];

      services.forEach((service) => {
        if (serviceStatusMap) {
          if (serviceStatusMap[service._id] && !['pending', 'approved'].includes(serviceStatusMap[service._id])) return;
        } else {
          if (!['pending', 'approved'].includes(service.serviceStatus)) return;
        }

        if (service.items?.length) {
          let itemArray: any[] = [];
          service.items.forEach((item: any) => {
            item.serviceId = service._id;
            if (['part', 'labor', 'tire', 'hazmatFee', 'tireStorage', 'sublet', 'other'].includes(item.type) && !item.salesCode?.salesCodeId) {
              const itemObj = {
                orderId: estimateId,
                itemId: item._id,
                type: item.type,
                title: item.title,
                serviceId: item.serviceId,
                category: item.category,
                salesCode: item.salesCode,
                tags: item.tags,
                itemClassificationTitle: this.getItemTitle(item),
                isSkipped: false,
              };
              itemArray.push(itemObj);
              skippedItems[item._id] = itemObj;
            } else if (item.type === 'bundle' && item.subItems?.length) {
              item.subItems.forEach((subItem: any) => {
                if (!subItem.salesCode?.salesCodeId) {
                  const subItemObj = {
                    orderId: estimateId,
                    itemId: subItem._id,
                    subItemId: subItem._id,
                    type: subItem.type,
                    title: subItem.title,
                    serviceId: item.serviceId,
                    category: subItem.category,
                    salesCode: subItem.salesCode,
                    tags: subItem.tags,
                    itemClassificationTitle: this.getItemTitle(subItem),
                    isSkipped: false,
                    isSubItem: true,
                    flatRateId: item?._id,
                  };
                  itemArray.push(subItemObj);
                  skippedItems[subItem._id] = subItemObj;
                }
              });
            }
          });

          if (itemArray?.length) {
            serviceArray.push({
              ...service,
              items: itemArray,
            });
          }
        }
      });
      unassignedServiceItems = serviceArray;
    }

    return { unassignedServiceItems, skippedItems };
  }

  getUnassignedWarrantyServiceItems(estimateId: string, services: any[] = [], serviceStatusMap: any = null, warrantyObj: any[] = []): any {
    let unassignedServiceItems: any[] = [];
    let skippedItems: any = {};
    let payload: any = { items: {} };

    if (!services.length) return { unassignedServiceItems, skippedItems, payload };

    services.forEach((service) => {
      const status = serviceStatusMap ? serviceStatusMap[service._id] : service.serviceStatus;
      if (!['pending', 'approved'].includes(status)) return;

      let validItems: any[] = [];

      service.items?.forEach((item) => {
        item.serviceId = service._id;
        
        if (item.type === 'bundle' && item.subItems?.length) {
          // Handle bundle items by checking subItems for warranty eligibility
          item.subItems.forEach((subItem) => {
            subItem.serviceId = service._id;
            const isSubItemWarrantyMatched = warrantyObj.length && warrantyObj.some((w) => w[subItem._id]?.warrantyType);
            const isSubItemEligible = ['part', 'tire'].includes(subItem.type) && !subItem.warranty?.type;
            
            if ((warrantyObj.length && isSubItemWarrantyMatched) || (!warrantyObj.length && isSubItemEligible)) {
              
              const keyToFind = subItem._id;
              const foundItem = Object.values(warrantyObj).find((obj) => obj[keyToFind]);
              if (foundItem) {
                if (!subItem?.warranty?.warrantySettingId && !subItem?.warranty?.title) {
                  subItem.warranty = {
                    ...subItem.warranty,
                    warrantySettingId: foundItem[subItem._id]?._id,
                    title: foundItem[subItem._id]?.title,
                  };
                }
              }
              
              const formattedSubItem = {
                ...subItem,
                subSubCategoryId: subItem?.category?.subSubCategoryId,
                orderId: estimateId,
                itemId: subItem._id,
                isSkipped: false,
                isSubItem: true,
                parentItemId: item._id
              };
              
              skippedItems[subItem._id] = formattedSubItem;
              validItems.push(formattedSubItem);
              
              if (!warrantyObj.length) {
                const productLine: any = subItem?.partSpecification?.productLine || {};
                const brand: any = subItem?.partSpecification?.brand || {};
                payload.items[subItem._id] = {
                  partTypeId: subItem?.category?.subSubCategoryId || subItem?.tireType?.tireTypeId,
                  subBrandId: productLine?.id,
                  brandId: brand?.id,
                  itemId: subItem.itemId,
                  ...(subItem.type === 'tire' ? { itemType: 'tire' } : { itemType: 'part' }),
                };
              }
            }
          });
        } else {
          // Handle non-bundle items
          const isWarrantyMatched = warrantyObj.length && warrantyObj.some((w) => w[item._id]?.warrantyType);
          const isEligibleItem = ['part', 'tire'].includes(item.type) && !item.warranty?.type;

          if ((warrantyObj.length && isWarrantyMatched) || (!warrantyObj.length && isEligibleItem)) {

            const keyToFind = item._id;
            const foundItem = Object.values(warrantyObj).find((obj) => obj[keyToFind]);
            if (foundItem) {
              if (!item?.warranty?.warrantySettingId && !item?.warranty?.title) {
                item.warranty = {
                  ...item.warranty,
                  warrantySettingId: foundItem[item._id]?._id,
                  title: foundItem[item._id]?.title,
                };
              }
            }

            const formattedItem = {
              ...item,
              subSubCategoryId: item?.category?.subSubCategoryId,
              orderId: estimateId,
              itemId: item._id,
              isSkipped: false,
            };

            skippedItems[item._id] = formattedItem;
            validItems.push(formattedItem);

            if (!warrantyObj.length) {
              const productLine: any = item?.partSpecification?.productLine || {};
              const brand: any = item?.partSpecification?.brand || {};
              payload.items[item._id] = {
                partTypeId: item?.category?.subSubCategoryId || item?.tireType?.tireTypeId,
                subBrandId: productLine?.id,
                brandId: brand?.id,
                itemId: item.itemId,
                ...(item.type === 'tire' ? { itemType: 'tire' } : { itemType: 'part' }),
              };
            }
          }
        }
      });

      if (warrantyObj.length && validItems.length) {
        unassignedServiceItems.push({ ...service, items: validItems });
      }
    });

    return { unassignedServiceItems, skippedItems, payload };
  }

  displaySalesCodeTitle(salesCode: any, category: any) {
    if (salesCode?.title && salesCode?.shortName) {
      return `${salesCode?.title} (${salesCode?.shortName})`;
    } else if (salesCode?.title) {
      return salesCode?.title;
    } else if (category?.categoryId) {
      return 'Unassigned';
    } else {
      return '';
    }
  }

  updateClassficationServiceItemTag(tags: any[] = [], { type, category, salesCode }) {
    if (!['part', 'labor', 'tire', 'tireStorage', 'hazmatFee', 'sublet', 'other'].includes(type)) {
      return tags;
    }

    const salesCodeUnassignTag = {
      name: 'Code Unassigned',
      color: '#E05E75',
      tagType: 'salesCode',
      tooltip: 'This item does not have a Sales Code assigned. All items on the RO should have a Sales Code assigned in order for the RO to be invoiced',
    };
    const salesCodeTag = {
      name: salesCode?.shortName || salesCode?.title || '',
      color: '#44A3D8',
      tagType: 'salesCode',
      tooltip: salesCode?.title,
    };

    if (tags?.length >= 0) {
      const index = findIndex(tags, { tagType: 'salesCode' });
      let tag = null;

      if (!category?._id) {
        tag = salesCodeUnassignTag;
      } else if (!salesCode?.salesCodeId) {
        tag = salesCodeUnassignTag;
      } else if (salesCode?.title) {
        tag = salesCodeTag;
      }

      if (salesCode?.salesCodeId) {
        tag = salesCodeTag;
      }

      if (tag) {
        if (index === -1) {
          tags.push(tag);
        } else {
          tags[index] = tag;
        }
      }

      return tags;
    }
  }

  strongPassword(user: any = {}, currentLang?: string) {
    const password = user.newPassword || ''; // Ensure password is a string
    const confirmPassword = user.confirmPassword || ''; // Ensure password is a string

    // Define validation checks
    const validationChecks = {
      hasMismatched: password && confirmPassword && password === confirmPassword,
      hasUpperCase: /[A-Z]/.test(password), // At least one uppercase letter
      hasLowerCase: /[a-z]/.test(password), // At least one lowercase letter
      hasNumeric: /[0-9]/.test(password), // At least one number
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password), // At least one special character
      hasValidLength: password.length >= 10, // At least 10 characters long
    };

    const translations = {
      en: {
        isValidLength: 'Be at least 10 characters',
        hasNumeric: 'Include at least one number',
        hasSpecialChar: 'Include at least one special character',
        hasUpperAndLowerCase: 'Include at least one upper and lower case letter',
        enterPassword: 'New Password and Confirm Password must be same.',
      },
      fr: {
        isValidLength: 'Comporter au moins 10 caractères',
        hasNumeric: 'Inclure au moins un chiffre',
        hasSpecialChar: 'Inclure au moins un caractère spécial',
        hasUpperAndLowerCase: 'Inclure au moins une lettre majuscule et une lettre minuscule',
        enterPassword: 'Le nouveau mot de passe et le mot de passe confirmé doivent être identiques',
      },
    };

    const lang = currentLang ? currentLang : 'en';

    // Create validation rules
    const validationRules = [
      {
        key: 'isValidLength',
        message: translations[lang].isValidLength,
        valid: validationChecks.hasValidLength,
      },
      {
        key: 'hasNumeric',
        message: translations[lang].hasNumeric,
        valid: validationChecks.hasNumeric,
      },
      {
        key: 'hasSpecialChar',
        message: translations[lang].hasSpecialChar,
        valid: validationChecks.hasSpecialChar,
      },
      {
        key: 'hasUpperAndLowerCase',
        message: translations[lang].hasUpperAndLowerCase,
        valid: validationChecks.hasUpperCase && validationChecks.hasLowerCase, // Check both conditions
      },
      {
        key: 'enterPassword',
        message: translations[lang].enterPassword,
        valid: validationChecks.hasMismatched,
      },
    ];

    return validationRules; // Return the validation rules
  }

  otherRequirementTooltip() {
    return `
        Here are additional billing requirements from the fleet company.

        Make sure your invoice complies with these requirements to avoid potential cancellations or delays in billing.
  `;
  }

  showMFCIconOnInput(customer) {
    if (customer && this.dataService.isMyFleetCenterEnabled && (customer._id || customer.customerId)) {
      if (customer.isMFCustomer) {
        return 'assets/myFleetCenterIntegration-icon.svg';
      } else {
        // return 'assets/img/al-icon-service.svg';
      }
    }
    return '';
  }

  assignSalesCode({ salesCode, categorySalesCode, subCategorySalesCode }) {
    if (salesCode?.salesCodeId) return salesCode;
    else if (subCategorySalesCode?.salesCodeId) return subCategorySalesCode;
    else if (categorySalesCode?.salesCodeId) return categorySalesCode;
    else return null;
  }

  postGainsightPXEvent(eventName: string, eventObj: any) {
    try {
      (<any>window).aptrinsic('track', eventName, eventObj);
    } catch (err) {
      this.showToaster('error', `Error while calling ${eventName} gainsight event`);
    }
  }

  validatePreferredLang(lang: string = '') {
    try {
      if (new RegExp('en|fr', 'i').test(lang)) {
        return lang;
      }

      return 'en';
    } catch (err) {
      console.log(err);
      return 'en';
    }
  }

  encodeURIComponent(str: string = '') {
    try {
      return encodeURIComponent(str).replace(/[!'()*]/g, function (c) {
        return '%' + c.charCodeAt(0).toString(16);
      });
    } catch (err) {
      return str;
    }
  }

  getTitleInLang({ title, titleFR }, lang: string = 'en') {
    try {
      return lang.toLowerCase() === 'en' ? title : titleFR;
    } catch (err) {
      console.log(err);
      return title | titleFR;
    }
  }

  applyOpacity(color: string, opacity: number): string {
    if (color.startsWith('#')) {
      return this.hexToRgba(color, opacity);
    } else if (color.startsWith('rgb')) {
      return this.rgbToRgba(color, opacity);
    } else {
      console.error('Unsupported color format:', color);
      return color; // Return the original color if unsupported
    }
  }

  hexToRgba(hex: string, opacity: number): string {
    hex = hex.replace('#', '');

    // Parse r, g, b values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  rgbToRgba(rgb: string, opacity: number): string {
    const rgbValues = rgb.match(/\d+/g); // Extract numbers from "rgb(202, 0, 0)"
    if (rgbValues && rgbValues.length >= 3) {
      const [r, g, b] = rgbValues;
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    console.error('Invalid RGB color:', rgb);
    return rgb;
  }

  getContrastColor(hex: string): string {
    try {
      // Remove leading hash if present
      hex = hex.replace(/^#/, '');
      // Parse r, g, b values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // Calculate brightness using a weighted average
      // More weight is given to green, then red, then blue
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      // If brightness is above a certain threshold, use black; otherwise, use white
      console.log(hex, brightness);
      return brightness > 125 ? '#000000' : '#FFFFFF';
    } catch (err) {
      console.log(err);
      return '#000000';
    }
  }

  motorEventGenerator = (vehicle: any, eventName: any = '', eventTitle: any = '') => {
    const date = new Date();
    const company = this.getCompany();
    let eventPayload:any;
    if (eventName == "found") {
      eventPayload = {
        tenantName: company.companyName,
        date: this.dateService.formatDate(date, datetimeFormat.mmmyyyy),
        time: this.dateService.getTime(date),
      }
    } else {
      eventPayload = {
        VIN: vehicle?.VIN || "-",
        YMM: `${vehicle?.year || "-"} ${vehicle?.make || "-"} ${vehicle?.model || "-"}`,
        tenantName: company?.companyName,
        date,
        time: this.dateService.getTime(date),
      }
    }
    this.postGainsightPXEvent(eventTitle, eventPayload);
  };

  serviceEventGenerator(roNumber: number,serviceName?: string,isDeferred: boolean = false,isCanned: boolean = false) {
    const eventName = isDeferred
      ? 'Deferred Service Added'
      : isCanned
        ? 'Canned Service Added'
        : 'Order Service Added';

    this.postGainsightPXEvent(eventName, {
      roNumber,
      serviceName,
    });
  }


}
